import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CommandHistory } from "@/commands/CommandHistory";
import type { Command, EditorSelectionState } from "@/commands/Command";
import {
  canFrameNodes,
  canGroupNodes,
  canCreateComponent,
  createDuplicateNodeCommands,
  canUngroupNodes,
  AddComponentVariantCommand,
  ConvertFrameGroupCommand,
  DeleteNodeCommand,
  DeleteComponentVariantCommand,
  CreateComponentCommand,
  DetachInstanceCommand,
  GroupNodesCommand,
  InsertNodeCommand,
  MoveNodesInTreeCommand,
  ReorderNodesCommand,
  RenameNodeCommand,
  type ReorderPlacement,
  UngroupNodesCommand,
  UpdateNodeBoundsCommand,
  UpdateNodeStateCommand,
  UpdateNodeMaskCommand,
  UpdateNodeStyleCommand,
  FlipNodesCommand,
  UpdateNodeTransformCommand,
  UpdateTextCommand,
  UpdateInstanceVariantCommand,
} from "@/commands/nodeCommands";
import { RenameDocumentCommand } from "@/commands/documentCommands";
import { DeletePrototypeLinkCommand, UpsertPrototypeLinkCommand } from "@/commands/prototypeCommands";
import {
  DeleteAnimationNodesCommand,
  InsertAnimationNodeCommand,
  UpsertAnimationConnectionCommand,
} from "@/commands/animationCommands";
import { TransactionCommand } from "@/commands/transaction";
import {
  DetachDocumentStyleCommand,
} from "@/commands/styleCommands";
import type { CreateStyleFromSelectionOptions, DocumentStyle, DocumentStyleKind } from "@/document/model/documentStyles";
import { inlineStyleBinding, styleBindingId } from "@/document/model/documentStyles";
import type { Guide, GuideScope, GuideSelection } from "@/document/model/guides";
import {
  AddVectorVertexCommand,
  BendVectorEdgeCommand,
  ConnectVectorVerticesCommand,
  DeleteVectorEdgesCommand,
  DeleteVectorVertexCommand,
  InsertVectorVertexAtIntersectionCommand,
  InsertVectorVertexOnEdgeCommand,
  InsertVectorSelectionCommand,
  KnifeCutAroundVertexCommand,
  KnifeCutVectorEdgesCommand,
  MoveVectorHandlesCommand,
  MergeVectorVerticesCommand,
  ToggleVectorRegionFillCommand,
  UpdateVectorVertexSettingsCommand,
} from "@/commands/vectorCommands";
import { DEFAULT_DOCUMENT_VIEWPORT, createFrameNode, createPrimitiveNode, createTextNode, findFirstFrameNode, findPageIdForSceneNode, findSceneNode, getActivePage, getActivePageSceneNodes, getAllSceneNodes, getSceneNodeChildren, resolveActivePageId, type CurvaDocument, type DocumentViewport } from "@/document/model/document";
import { animationDuration } from "@/document/animation/applyAnimation";
import { createAnimationGraphNode, createEmptyAnimationGraph, normalizeAnimationGraph, type AnimateAnimationNode, type AnimationConnection, type AnimationEndpoint, type AnimationGraph, type AnimationGraphNode, type AnimationGraphVisualGroup, type AnimationNodeKind, type GroupAnimationNode } from "@/document/model/animation";
import {
  createDefaultFrameFlexLayout,
  createIdentityTransform,
  flexChildLayout,
  frameAutoLayoutMode,
  frameCornerRadii,
  frameFlexLayout,
  nodeConstraints,
  type AutoLayoutMode,
  type BooleanOperation,
  type FlexChildLayout,
  type FrameFlexLayout,
  type FrameMirrorLayout,
  type FrameNode,
  type FrameRepeatLayout,
  type GroupNode,
  type NodeConstraints,
  type SceneNode,
  type VectorNetworkNode,
} from "@/document/model/nodes";
import {
  findDocumentScript,
  frameHasScript,
  resolveScriptContextFrameFromSelection,
} from "@/document/model/documentScripts";
import type { PrimitiveDefinition, PrimitiveKind } from "@/document/model/primitives";
import type { VectorEdge, VectorRegion, VectorVertex } from "@/document/model/vectorNetwork";
import type { OklchColor } from "@/document/model/color";
import { createDefaultStyle, createSolidPaint, normalizeStrokeStack, styleWithStrokeStack, type NodeStyle, type Paint } from "@/document/model/styles";
import { createDefaultRichText, patchTextTypeStyleForInspector, type RichTextModel, type TypeStyle } from "@/document/model/text";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { materializeShapeBuilderNetwork, type ShapeBuilderSelection } from "@/document/operations/shapeBuilder";
import {
  editableNodeIds,
  isComponentInstanceDropParent,
  isNodeDescendantOf,
  isNodeLockedForEditing
} from "@/document/operations/nodeLocking";
import { createInstanceFromComponent } from "@/document/operations/componentSystem";
import { applyNodeLocalPlacementFromWorld } from "@/document/operations/nodeWorldTransform";
import { createEditorActions } from "@/editor/actions/editorActions";
import { selectAllNodeIds } from "@/editor/shell/selectAll";
import { installNativeAppMenu, refreshNativeRecentsMenu, type NativeMenuHandlers } from "@/editor/shell/installNativeAppMenu";
import { stripNativeMenuOwnedShortcuts } from "@/editor/shell/nativeMenuOwnedActions";
import { alignmentLabels, getAlignmentReferenceBounds, getAlignmentUpdates, type AlignmentAction } from "@/editor/shell/right-panel/alignment";
import { getSelectionBounds, transformBoundsInGroup } from "@/editor/tools/hitTest";
import { vectorizePencilStroke } from "@/editor/tools/pencilVectorize";
import { copySceneNodeAsPng, exportSceneNodeToPngBlob } from "@/export/pngExporter";
import { exportAnimation, type AnimationExportFormat } from "@/export/animationExporter";
import { exportDocumentAnimationToMermaid } from "@/export/mermaidAnimationExporter";
import { exportSceneNodeToSvg } from "@/export/svgExporter";
import { CURVA_FILE_EXTENSION } from "@/io/fileTypes";
import {
  clearDesktopRecents,
  flushDesktopConfig,
  loadDesktopConfig,
  setDesktopSession,
} from "@/io/desktopConfig";
import { isSafeMode, loadPersistedDocument, savePersistedDocument } from "@/io/documentPersistence";
import {
  createEmptyEditorScriptsDocument,
  EDITOR_SCRIPTS_DOCUMENT_NAME,
  editorScriptsSessionPath,
} from "@/io/editorScriptsPersistence";
import {
  confirmDiscard,
  isNativeShell,
  onNativeFileDrop,
  onOpenCurvaPaths,
  onWindowCloseRequested,
  pickOpenFile,
  readOpenWithFile,
  setWindowTitle,
  takePendingOpenPaths,
  type OpenedFile,
} from "@/io/platform";
import { deserializeCurvaPackage } from "@/io/package";
import { createId } from "@/lib/ids";
import { rectsEqual, type Rect } from "@/lib/math/rect";
import { inverseRotatePoint, rectCenter, transformRotationOrigin } from "@/lib/math/rotation";
import { invalidateTextLayoutCache } from "@/renderer/text/canvasKitText";
import { resetDrawingStyleMemory } from "@/editor/drawing/drawingStyleMemory";
import { documentStore, editorScriptsStore, selectionStore, uiStore, viewStore, clipboardStore, workspaceStore } from "@/editor/store";
import { createDocumentSession, hydrateLiveSnapshot, type LiveEditorSnapshot } from "@/editor/workspace";
import { isCurvaFileDrag, isCurvaFilePath, readCurvaFilesFromDataTransfer } from "@/editor/shell/curvaFileDrop";
import { formatWindowTitle } from "@/editor/shell/windowTitle";
import {
  EditorCommandHost,
  createDefaultCommandHostDeps,
  updateNodeBounds as runUpdateNodeBounds,
  updateNodeTransforms as runUpdateNodeTransforms,
  updateSelectionBounds as runUpdateSelectionBounds,
  nudgeNodes as runNudgeNodes,
  nudgeSelection as runNudgeSelection,
  nudgeSelectedVectorGeometry as runNudgeSelectedVectorGeometry,
  nudgeVectorVertexPositions as runNudgeVectorVertexPositions,
  selectNodes as runSelectNodes,
  selectPrototypeLink as runSelectPrototypeLink,
  selectVectorVertex as runSelectVectorVertex,
  selectVectorVertices as runSelectVectorVertices,
  selectVectorHandle as runSelectVectorHandle,
  selectVectorEdges as runSelectVectorEdges,
  selectAnimationNodes as runSelectAnimationNodes,
  selectAnimationConnections as runSelectAnimationConnections,
  selectObjectConnections as runSelectObjectConnections,
  editableSelection as runEditableSelection,
  activateAnimationRoot as runActivateAnimationRoot,
  activateAnimationRootForNode as runActivateAnimationRootForNode,
  moveVectorVertex as runMoveVectorVertex,
  moveVectorEdgeVertices as runMoveVectorEdgeVertices,
  moveVectorHandle as runMoveVectorHandle,
  insertVectorVertexOnEdge as runInsertVectorVertexOnEdge,
  insertVectorVertexAtIntersection as runInsertVectorVertexAtIntersection,
  connectPenToInsertedVertex as runConnectPenToInsertedVertex,
  addPenVertex as runAddPenVertex,
  connectPenVertices as runConnectPenVertices,
  bendVectorEdge as runBendVectorEdge,
  knifeCutEdges as runKnifeCutEdges,
  knifeCutAroundVertex as runKnifeCutAroundVertex,
  toggleVectorRegionFill as runToggleVectorRegionFill,
  updateVectorVertexSettings as runUpdateVectorVertexSettings,
  newDocument as runNewDocument,
  importComponentsFromCurvaFile as runImportComponentsFromCurvaFile,
  exportCurvaFile as runExportCurvaFile,
  exportSelectedFramesPdf as runExportSelectedFramesPdf,
  switchPage as runSwitchPage,
  addPage as runAddPage,
  renamePage as runRenamePage,
  deletePage as runDeletePage,
  duplicatePage as runDuplicatePage,
  addGuide as runAddGuide,
  moveGuide as runMoveGuide,
  relocateGuide as runRelocateGuide,
  deleteGuide as runDeleteGuide,
  updateSelectedGuide as runUpdateSelectedGuide,
  updateNodeStyle as runUpdateNodeStyle,
  updateSelectedNodeStyles as runUpdateSelectedNodeStyles,
  ensureSelectionPaint as runEnsureSelectionPaint,
  updateSelectionPaint as runUpdateSelectionPaint,
  subdivideSelectedEdges as runSubdivideSelectedEdges,
  splitVectorNetworkToRegionObjects as runSplitVectorNetworkToRegionObjects,
  updatePrimitiveDefinition as runUpdatePrimitiveDefinition,
  updateGradientPaint as runUpdateGradientPaint,
  updateFrameClipsContent as runUpdateFrameClipsContent,
  updateFrameCornerRadii as runUpdateFrameCornerRadii,
  updateFrameLayout as runUpdateFrameLayout,
  createDocumentScriptEntry as runCreateDocumentScript,
  createScriptFolderEntry as runCreateScriptFolder,
  renameScriptEntry as runRenameScriptEntry,
  updateDocumentScriptSource as runUpdateDocumentScriptSource,
  duplicateDocumentScript as runDuplicateDocumentScript,
  deleteScriptEntry as runDeleteScriptEntry,
  moveScriptEntry as runMoveScriptEntry,
  setFrameScriptId as runSetFrameScriptId,
  importScriptsFromCurvaFile as runImportScriptsFromCurvaFile,
  importAndReplaceScriptsFromCurvaFile as runImportAndReplaceScriptsFromCurvaFile,
  addEditorScriptsFromCurvaFile as runAddEditorScriptsFromCurvaFile,
  replaceEditorScriptsFromCurvaFile as runReplaceEditorScriptsFromCurvaFile,
  clearEditorScripts as runClearEditorScripts,
  copyEditorScriptEntryToDocument as runCopyEditorScriptEntryToDocument,
  downloadEditorScriptsDocument as runDownloadEditorScriptsDocument,
  updateFlexChildLayout as runUpdateFlexChildLayout,
  updateNodeConstraints as runUpdateNodeConstraints,
  createDocumentStyle as runCreateDocumentStyle,
  applyDocumentStyle as runApplyDocumentStyle,
  detachDocumentStyle as runDetachDocumentStyle,
  updateDocumentStyle as runUpdateDocumentStyle,
  deleteDocumentStyle as runDeleteDocumentStyle,
  addAnimateProperty as runAddAnimateProperty,
  bakeActiveAnimationToFrame as runBakeActiveAnimationToFrame,
  connectAnimation as runConnectAnimation,
  connectSceneObjectToAnimation as runConnectSceneObjectToAnimation,
  createSceneObjectAnimateNode as runCreateSceneObjectAnimateNode,
  createSceneObjectsGroupNode as runCreateSceneObjectsGroupNode,
  deleteAnimationVisualGroup as runDeleteAnimationVisualGroup,
  deleteSelectedAnimationSelection as runDeleteSelectedAnimationSelection,
  duplicateSelectedAnimationNodes as runDuplicateSelectedAnimationNodes,
  groupSelectedAnimationNodes as runGroupSelectedAnimationNodes,
  insertAnimationNode as runInsertAnimationNode,
  insertAnimationNodeAndConnect as runInsertAnimationNodeAndConnect,
  moveAnimationNodes as runMoveAnimationNodes,
  updateAnimationNode as runUpdateAnimationNode,
  updateAnimationVisualGroup as runUpdateAnimationVisualGroup,
  addComponentVariant as runAddComponentVariant,
  alignSelectedNodes as runAlignSelectedNodes,
  applyBooleanOperation as runApplyBooleanOperation,
  commitShapeBuilderSelection as runCommitShapeBuilderSelection,
  convertFrameGroup as runConvertFrameGroup,
  createComponent as runCreateComponent,
  createPenNetwork as runCreatePenNetwork,
  createPrototypeLink as runCreatePrototypeLink,
  deleteComponentVariant as runDeleteComponentVariant,
  detachInstance as runDetachInstance,
  drawPencilStroke as runDrawPencilStroke,
  flipSelectedNodes as runFlipSelectedNodes,
  frameSelectedNodes as runFrameSelectedNodes,
  groupSelectedNodes as runGroupSelectedNodes,
  insertComponentInstance as runInsertComponentInstance,
  insertFrameTemplate as runInsertFrameTemplate,
  outlineStrokeForSelection as runOutlineStrokeForSelection,
  reorderFlexChild as runReorderFlexChild,
  reorderNodes as runReorderNodes,
  ungroupSelectedNodes as runUngroupSelectedNodes,
  updateInstanceVariant as runUpdateInstanceVariant,
  flattenSelectedNodes as runFlattenSelectedNodes,
  copyNodeStyle as runCopyNodeStyle,
  copyPrimarySelectionStyle as runCopyPrimarySelectionStyle,
  copySelectedAnimationNodes as runCopySelectedAnimationNodes,
  copySelectedObjects as runCopySelectedObjects,
  copySelectedVectorVertices as runCopySelectedVectorVertices,
  cutSelectedObjects as runCutSelectedObjects,
  deleteNodes as runDeleteNodes,
  dropImageOnCanvas as runDropImageOnCanvas,
  duplicateSelectedObjects as runDuplicateSelectedObjects,
  duplicateVectorVerticesForDrag as runDuplicateVectorVerticesForDrag,
  pasteAnimationNodes as runPasteAnimationNodes,
  pasteImage as runPasteImage,
  pasteObjectPayload as runPasteObjectPayload,
  pasteObjects as runPasteObjects,
  pasteStyleToSelection as runPasteStyleToSelection,
  pasteSvg as runPasteSvg,
  pasteText as runPasteText,
  pasteVectorSelectionAsObjectFromClipboard as runPasteVectorSelectionAsObjectFromClipboard,
  pasteVectorVerticesFromClipboard as runPasteVectorVerticesFromClipboard,
  resetDuplicateContinuation as runResetDuplicateContinuation,
  canPasteVectorVertices as runCanPasteVectorVertices,
  clearTextTypeStylePreview as runClearTextTypeStylePreview,
  previewTextTypeStyle as runPreviewTextTypeStyle,
  textFillForInspector as runTextFillForInspector,
  toggleTextBold as runToggleTextBold,
  updateTextCharacters as runUpdateTextCharacters,
  updateTextNode as runUpdateTextNode,
  updateTextTypeStyle as runUpdateTextTypeStyle,
} from "@/editor/commands";
import { contextMenuConvertTarget, insertionPointAboveNode } from "@/editor/shell/shellTreeHelpers";
import {
  useAnimationPlaybackKeymap,
  useEditorShellClipboard,
  useEditorShellKeymap,
} from "@/editor/shell/bindKeymap";
import {
  createAnimationActions,
  createDocumentActions,
  createVectorActions,
  deleteSelectedVectorSelectionFromHost,
} from "@/editor/shell/canvasActions";
import { createTopBarActions } from "@/editor/shell/topBarActions";
import { cancelSubdivideSession, refreshSubdividePreview, subdivideSessionMatchesSelection } from "@/editor/tools/subdivideSession";
import { bindCommand } from "@/editor/shell/shellCommandBinding";
import { ComponentImportProgressToast } from "@/editor/shell/ComponentImportProgressToast";
import {
  defaultAnimationExportScale,
  normalizedExportBaseName,
  pngScaleLabel,
  pngScaleSuffix,
} from "@/editor/shell/shellExportNames";
import {
  beginTextEditSession,
  commitTextEditSession as runCommitTextEditSession,
  enterTextMode as runEnterTextMode,
  exitTextMode as runExitTextMode,
  type TextEditSessionSnapshot,
} from "@/editor/shell/TextEditSession";
import {
  cancelPathDrawing as runCancelPathDrawing,
  clearVectorTargetingSelection as runClearVectorTargetingSelection,
  enterEditMode as runEnterEditMode,
  enterVectorMode as runEnterVectorMode,
  enterVectorTargeting as runEnterVectorTargeting,
  exitVectorMode as runExitVectorMode,
  exitVectorTargeting as runExitVectorTargeting,
} from "@/editor/shell/VectorEditSession";
import { useStoreSubscribe } from "@/lib/store";
import { CanvasWorkspace } from "./CanvasWorkspace";
import type { ColorSamplerRequest, ColorSamplerRequestWithCommit } from "./colorSampler";
import { SELECTION_COLOR_SAMPLE_SOURCE_ID } from "./selectionColorSample";
import { frameDropTargetForMovedNodes } from "./canvasDropTargets";
import { refreshComponentThumbnail, clearComponentThumbnails } from "@/document/previews/componentThumbnails";
import { LeftPanel, type LayerDropTarget } from "./LeftPanel";
import { ObjectContextMenu } from "./ObjectContextMenu";
import { RightPanel } from "./RightPanel";
import type { FrameTemplate } from "./frameTemplates";
import { FRAME_TEMPLATE_CATEGORIES } from "./frameTemplates";
import { frameCaptureSiblingIds } from "./frameCapture";
import { styleWithPaint } from "./right-panel/paintStyles";
import type { SelectionPaintKind } from "./right-panel/selectionPaintPicker";
import { SelectionPaintPickerOverlay } from "./right-panel/SelectionPaintPickerOverlay";
import {
  collectSelectionScopeNodes,
  paintsEqual,
  replacePaintInNodeStyle,
  replacePaintInTextModel,
  type SelectionColorScope,
  nodeContributesOwnPaints,
} from "./right-panel/selectionColors";
import type { PaintTarget } from "./right-panel/types";
import type { PickerMode } from "./right-panel/types";
import {
  boundsEqual,
  transformEqual,
  preferredAnimationRootForAnimationMode,
  primitiveEqual,
  reorderMessage,
  styleEqual,
  textEqual,
  titleCase,
  toggleSelection,
  topFrameIdAtPoint,
} from "./shellSelection";
import {
  downloadBytes,
  documentDisplayName,
  normalizedCurvaFileName,
  appendSwapFillStrokeCommands,
  sanitizeDocumentBaseName,
  type CurvaClipboardPayload,
  type StyleClipboardPart,
} from "./shellTransfer";
import {
  resolveDocumentFonts,
  textBoundsForLayout,
  textIntrinsicBounds,
} from "./shellTextHelpers";
import { registerTextBoundsMeasurer, registerTextVisualBoundsProvider } from "@/document/operations/textAutoSize";
import { textVisualBounds } from "@/renderer/text/textVisualBounds";

// Let the document layer keep auto-sized text in sync with its content during
// geometry finalization (e.g. auto-layout), not just on text edits.
registerTextBoundsMeasurer(textIntrinsicBounds);
// And let auto-layout center text on its visual ink (baseline/ascent aware),
// matching how the right-panel alignment buttons treat text.
registerTextVisualBoundsProvider(textVisualBounds);
import {
  createPencilVectorNetworkNode,
} from "./shellVectorConversion";
import { importSvgMarkup } from "@/import/svg/svgImporter";
import { DocumentTabStrip } from "./DocumentTabStrip";
import { StatusBar } from "./StatusBar";
import { FloatingToolPanel, TopBar, type ActiveTool } from "./TopBar";
import { useBrowserGestureGuards } from "./useBrowserGestureGuards";
import { PrototypePlayerModal } from "./PrototypePlayerModal";
import { AnimationPreviewWindow } from "./AnimationPreviewWindow";
import { ScriptEditorWindow } from "./ScriptEditorWindow";
import { ScriptAttachPicker } from "./right-panel/ScriptAttachPicker";
import { ScriptsDebugWindow } from "./ScriptsDebugWindow";
import { ScriptPreviewModal } from "./ScriptPreviewModal";
import {
  createScriptParamsSession,
  ScriptParamsModal,
  type ScriptParamsModalSession,
} from "./ScriptParamsModal";
import {
  ensureDefaultScriptEngine,
  ScriptRealtimeController,
  startAttachedFrameScript,
  stopAttachedFrameScriptSession,
  type ScriptLogDelivery,
} from "@/scripting/scriptRunner";
import { ScriptPreviewInputBuffer } from "@/scripting/scriptPreviewInput";
import {
  applyScriptParamValues,
  defaultValuesForParams,
  parseScriptParams,
  type ScriptParamValue,
} from "@/scripting/scriptParams";
import { applyScriptDocumentPages } from "@/editor/commands/handlers/scriptHandlers";
import { materializeScriptPreviewDocument } from "@/commands/scriptCommands";
import { AnimationPanel } from "./AnimationPanel";
import { DEFAULT_ANIMATION_TIMELINE_VIEWPORT, animationTimelineViewportKey, animationTimelineViewportsWith, type AnimationLoopRegion, type AnimationTimelineViewport } from "./AnimationTimeline";
import type { CanvasPointerStatus } from "./canvasWorkspaceTypes";
import { initMcpBridgeFromPreference } from "@/mcp/mcpBridgeClient";
import { setMcpToolSession, type McpRunScriptResult } from "@/mcp/toolHandlers";

export type EditorMode = "select" | "vectorEdit" | "textEdit";
type UiChromeMode = "visible" | "hidden" | "tools" | "toolsSidebar";
type RightPanelTab = "design" | "prototype" | "dev" | "scripts";

interface ContextMenuState {
  nodeId: string;
  x: number;
  y: number;
}

export function EditorShell() {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const document = useStoreSubscribe(documentStore.document); // Re-render trigger; read via documentStore.getDocument() in handlers.
  const workspace = useStoreSubscribe(workspaceStore.state);
  const selection = useStoreSubscribe(selectionStore.selection);
  const tool = useStoreSubscribe(selectionStore.tool);
  const selectedObjectConnectionKeys = useStoreSubscribe(selectionStore.selectedObjectConnectionKeys);
  const animation = useStoreSubscribe(uiStore.animation);
  const isDocumentLoading = useStoreSubscribe(uiStore.isDocumentLoading);
  const componentImportProgress = useStoreSubscribe(uiStore.componentImportProgress);
  const zoom = useStoreSubscribe(uiStore.zoom);
  const contextMenu = useStoreSubscribe(uiStore.contextMenu);
  const colorSampler = useStoreSubscribe(uiStore.colorSampler);
  const rightPanelTab = useStoreSubscribe(uiStore.rightPanelTab);
  const uiChromeMode = useStoreSubscribe(uiStore.uiChromeMode);
  const allowScripts = useStoreSubscribe(uiStore.allowScripts);
  const scriptEditorScriptId = useStoreSubscribe(uiStore.scriptEditorScriptId);
  const scriptsDebugWindowOpen = useStoreSubscribe(uiStore.scriptsDebugWindowOpen);
  const scriptPreview = useStoreSubscribe(uiStore.scriptPreview);
  const prototypePlayerFrameId = useStoreSubscribe(uiStore.prototypePlayerFrameId);
  const selectedPrototypeLinkId = useStoreSubscribe(uiStore.selectedPrototypeLinkId);
  const selectedGuide = useStoreSubscribe(uiStore.selectedGuide);
  const textSelectionState = useStoreSubscribe(uiStore.textSelection);

  const selectedNodeIds = selection.selectedNodeIds;
  const primarySelectedNodeId = selection.primarySelectedNodeId;
  const activePageId = selection.activePageId;
  const mode = selection.mode;
  const selectedVertexId = selection.selectedVertexId;
  const selectedVertexIds = selection.selectedVertexIds ?? [];
  const selectedEdgeIds = selection.selectedEdgeIds ?? [];
  const selectedHandle = selection.selectedHandle;
  const selectedAnimationNodeIds = selection.selectedAnimationNodeIds ?? [];
  const primarySelectedAnimationNodeId = selection.primarySelectedAnimationNodeId;
  const selectedAnimationConnectionIds = selection.selectedAnimationConnectionIds ?? [];
  const activeTool = tool.activeTool;
  const selectedShapeKind = tool.selectedShapeKind;
  const animationPanelOpen = animation.panelOpen;
  const animationTimelineOpen = animation.timelineOpen;
  const animationPreviewWindowOpen = animation.previewWindowOpen;
  const animationPlaying = animation.playing;
  const activeAnimationRootId = animation.activeRootId;
  const animationTimeMs = animation.timeMs;
  const animationLoopRegion = animation.loopRegion;
  const animationTimelineViewports = animation.timelineViewports;
  const animationExportingFormat = animation.exportingFormat;
  const armedAnimationNodeKind = animation.armedNodeKind;
  const vectorTargetNodeId = animation.vectorTargetNodeId;
  const selectedGuideRef = useRef<GuideSelection | undefined>(undefined);
  const historyRef = useRef(new CommandHistory());
  const textEditSessionRef = useRef<TextEditSessionSnapshot | undefined>(undefined);
  const colorSamplerCommitRef = useRef<((color: OklchColor, sampleStroke: boolean) => void) | undefined>(undefined);
  const latestViewportRef = useRef<DocumentViewport>({ ...DEFAULT_DOCUMENT_VIEWPORT });
  const latestDocumentRef = useRef<CurvaDocument>(documentStore.getDocument());
  const pendingBoundsPreviewRef = useRef<Rect | undefined>(undefined);
  const boundsPreviewAnimationFrameRef = useRef<number | undefined>(undefined);
  const persistenceReadyRef = useRef(false);
  const persistenceSaveTimerRef = useRef<number | undefined>(undefined);
  const editorScriptsSaveTimerRef = useRef<number | undefined>(undefined);
  const componentPlacementTokenRef = useRef(0);
  const textPreviewGenerationRef = useRef(0);
  const leaveTextEditRef = useRef<() => void>(() => {});
  const scriptRealtimeRef = useRef<ScriptRealtimeController | undefined>(undefined);
  const scriptPreviewInputRef = useRef(new ScriptPreviewInputBuffer());
  const scriptParamPreviewGenRef = useRef(0);
  const scriptParamPreviewTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [scriptAttachFrameId, setScriptAttachFrameId] = useState<string | undefined>(undefined);
  const [scriptParamsSession, setScriptParamsSession] = useState<ScriptParamsModalSession | undefined>(undefined);

  const commandHost = useMemo(
    () => new EditorCommandHost(
      historyRef,
      createDefaultCommandHostDeps(latestViewportRef, latestDocumentRef, () => leaveTextEditRef.current()),
    ),
    [],
  );
  const {
    commit,
    patchDocument,
    commandContext,
    currentSelection,
    selectionSnapshot,
    applySelectionSnapshot,
    documentWithCurrentViewport,
    updateSelection,
    undo,
    redo,
  } = commandHost;

  const resolvedActivePageId = resolveActivePageId(documentStore.getDocument(), activePageId);
  const animationGraph = useMemo(() => normalizeAnimationGraph(document.animation ?? createEmptyAnimationGraph()), [document.animation]);
  const scriptPreviewDocument = useMemo(() => {
    if (!scriptPreview) {
      return undefined;
    }
    const preview = materializeScriptPreviewDocument(document, scriptPreview.pages);
    preview.revision = document.revision + scriptPreview.revision;
    return preview;
  }, [document, scriptPreview]);
  const animationDurationMs = useMemo(() => animationDuration(animationGraph, activeAnimationRootId, documentStore.getDocument()), [animationGraph, activeAnimationRootId, document]);
  const activeAnimationTimelineViewportKey = animationTimelineViewportKey(activeAnimationRootId);
  const activeAnimationTimelineViewport = animationTimelineViewports[activeAnimationTimelineViewportKey] ?? DEFAULT_ANIMATION_TIMELINE_VIEWPORT;
  const selectedAnimationNode = animationGraph.nodes.find(node => node.id === primarySelectedAnimationNodeId);
  const activePageFrameId = findFirstFrameNode(documentStore.getDocument(), resolvedActivePageId)?.id;
  const selectionBounds = getSelectionBounds(documentStore.getDocument(), selectedNodeIds);
  const notifyFontsResolved = useCallback(() => {
    invalidateTextLayoutCache();
    viewStore.bumpFontsReadyToken();
  }, []);
  const textHandlerDeps = { textPreviewGenerationRef, onFontsResolved: notifyFontsResolved };
  function resetDocumentScopedState(): void {
    clearComponentThumbnails();
    invalidateTextLayoutCache();
    resetDrawingStyleMemory();
    viewStore.setTextPreviews(new Map<string, RichTextModel>());
    viewStore.clearVectorPreviews();
    selectionStore.setFreshCutVertexIds([]);
    uiStore.setTextSelection(undefined);
    uiStore.setContextMenu(undefined);
    uiStore.setEditingImageFillTarget(undefined);
    uiStore.setEditingGradientFillTarget(undefined);
    uiStore.setColorSampler(undefined);
    colorSamplerCommitRef.current = undefined;
    pendingBoundsPreviewRef.current = undefined;
    if (boundsPreviewAnimationFrameRef.current !== undefined) {
      window.cancelAnimationFrame(boundsPreviewAnimationFrameRef.current);
      boundsPreviewAnimationFrameRef.current = undefined;
    }
    viewStore.setBoundsPreview(undefined);
    uiStore.setSelectedGuide(undefined);
    uiStore.setSelectedPrototypeLinkId(undefined);
    selectionStore.patchSelection({ selectedAnimationNodeIds: [] });
    selectionStore.patchSelection({ primarySelectedAnimationNodeId: undefined });
    selectionStore.patchSelection({ selectedAnimationConnectionIds: [] });
    selectionStore.setSelectedObjectConnectionKeys([]);
    uiStore.patchAnimation({ panelOpen: false });
    uiStore.patchAnimation({ armedNodeKind: undefined });
    uiStore.patchAnimation({ playing: false });
    uiStore.patchAnimation({ timeMs: 0 });
    uiStore.patchAnimation({ loopRegion: undefined });
    uiStore.patchAnimation({ timelineViewports: {} });
    uiStore.patchAnimation({ activeRootId: undefined });
    uiStore.setPrototypePlayerFrameId(undefined);
    clearScriptParamLivePreview();
    stopScriptRealtimePreview();
    viewStore.setComponentPlacement(undefined);
    clipboardStore.clearObjectClipboards();
    runResetDuplicateContinuation();
  }
  const updateBoundsPreview = useCallback((preview: Rect | undefined) => {
    pendingBoundsPreviewRef.current = preview;
    if (boundsPreviewAnimationFrameRef.current !== undefined) {
      return;
    }

    boundsPreviewAnimationFrameRef.current = window.requestAnimationFrame(() => {
      boundsPreviewAnimationFrameRef.current = undefined;
      const nextPreview = pendingBoundsPreviewRef.current;
      const current = viewStore.boundsPreview.getValue();
      viewStore.setBoundsPreview(rectsEqual(current, nextPreview) ? current : nextPreview);
    });
  }, []);
  const requestColorSample = useCallback((request: ColorSamplerRequestWithCommit) => {
    uiStore.updateColorSampler(current => {
      if (current?.sourceId === request.sourceId) {
        colorSamplerCommitRef.current = undefined;
        uiStore.setMessage("Color sampler off");
        return undefined;
      }

      colorSamplerCommitRef.current = request.onSample;
      uiStore.setMessage(
        request.sourceId === SELECTION_COLOR_SAMPLE_SOURCE_ID
          ? "Sample fill color (hold Shift for stroke)"
          : "Click canvas to sample color",
      );
      return { sourceId: request.sourceId, mode: request.mode };
    });
  }, []);
  const cancelColorSample = useCallback((sourceId?: string) => {
    uiStore.updateColorSampler(current => {
      if (sourceId && current?.sourceId !== sourceId) {
        return current;
      }

      colorSamplerCommitRef.current = undefined;
      return undefined;
    });
  }, []);
  const updateColorSampleMode = useCallback((sourceId: string, mode: PickerMode) => {
    uiStore.updateColorSampler(current => current?.sourceId === sourceId ? { ...current, mode } : current);
  }, []);
  const commitColorSample = useCallback((color: OklchColor, options?: { sampleStroke?: boolean }) => {
    const sampleStroke = options?.sampleStroke ?? false;
    const wasSelectionTool = colorSampler?.sourceId === SELECTION_COLOR_SAMPLE_SOURCE_ID;
    colorSamplerCommitRef.current?.(color, sampleStroke);
    colorSamplerCommitRef.current = undefined;
    uiStore.setColorSampler(undefined);
    uiStore.setMessage(
      wasSelectionTool
        ? sampleStroke
          ? "Applied sampled stroke"
          : "Applied sampled fill"
        : "Sampled color",
    );
  }, [colorSampler?.sourceId]);
  const schedulePersistedDocument = useCallback((nextDocument: CurvaDocument) => {
    latestDocumentRef.current = nextDocument;
    const sessionKind = workspaceStore.getActiveSession().kind ?? "document";
    if (sessionKind === "editorScripts") {
      editorScriptsStore.syncFromLiveDocument(nextDocument);
      if (editorScriptsSaveTimerRef.current !== undefined) {
        window.clearTimeout(editorScriptsSaveTimerRef.current);
      }
      editorScriptsSaveTimerRef.current = window.setTimeout(() => {
        void editorScriptsStore.persistCurrent().catch(error => {
          console.warn("Failed to persist editor scripts", error);
        });
        editorScriptsSaveTimerRef.current = undefined;
      }, 350);
      return;
    }

    if (!persistenceReadyRef.current) {
      return;
    }

    if (persistenceSaveTimerRef.current !== undefined) {
      window.clearTimeout(persistenceSaveTimerRef.current);
    }

    persistenceSaveTimerRef.current = window.setTimeout(() => {
      void savePersistedDocument(documentWithCurrentViewport(latestDocumentRef.current)).catch(error => {
        console.warn("Failed to persist document", error);
      });
      persistenceSaveTimerRef.current = undefined;
    }, 350);
  }, []);
  useBrowserGestureGuards(rootRef);

  useEffect(() => {
    initMcpBridgeFromPreference();
  }, []);

  useEffect(() => {
    void editorScriptsStore.bootstrap().catch(error => {
      console.warn("Failed to bootstrap editor scripts", error);
    });
  }, []);

  const runDocumentScriptForMcpRef = useRef<
    (scriptId: string, options?: { contextFrameId?: string; values?: Record<string, ScriptParamValue> }) => Promise<McpRunScriptResult>
  >(async () => ({
    ok: false,
    scriptId: "",
    scriptName: "",
    logs: [],
    applied: false,
    realtime: false,
    message: "Script runner not ready",
    error: "Script runner not ready",
  }));

  useEffect(() => {
    setMcpToolSession({
      commandHost,
      runDocumentScript: (scriptId, options) => runDocumentScriptForMcpRef.current(scriptId, options),
    });
    return () => setMcpToolSession(undefined);
  }, [commandHost]);

  useEffect(() => {
    return () => {
      if (boundsPreviewAnimationFrameRef.current !== undefined) {
        window.cancelAnimationFrame(boundsPreviewAnimationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Desktop restores filesystem session / Open With in a separate effect.
    // IndexedDB remains web crash recovery and desktop fallback when no paths restore.
    if (isNativeShell()) {
      return;
    }

    let cancelled = false;
    const safeMode = isSafeMode();

    void (async () => {
      try {
        // ?safe boots a blank session and never applies the IndexedDB document,
        // so a crashy restore cannot wedge the GPU / tab. Writes are also disabled.
        const restoredDocument = safeMode ? undefined : await loadPersistedDocument();
        if (cancelled) {
          return;
        }

        if (restoredDocument) {
          historyRef.current = new CommandHistory();
          latestViewportRef.current = { ...DEFAULT_DOCUMENT_VIEWPORT, ...restoredDocument.viewport };
          latestDocumentRef.current = restoredDocument;
          documentStore.setDocument(restoredDocument);
          applySelectionSnapshot({
            activePageId: restoredDocument.pages[0]?.id,
            selectedNodeIds: [],
            primarySelectedNodeId: undefined,
            mode: "select",
            selectedVertexId: undefined,
            selectedEdgeIds: [],
            selectedHandle: undefined,
          });
          selectionStore.setActiveTool("select");
          selectionStore.setSelectedShapeKind("rectangle");
          resetDocumentScopedState();
          uiStore.setMessage("Restored document");
        } else {
          centerViewportOnDocumentPoint(0, 0);
          uiStore.setMessage(safeMode ? "Safe mode (persistence writes off)" : "New document");
        }

        await resolveDocumentFonts(latestDocumentRef.current);
        if (cancelled) {
          return;
        }

        notifyFontsResolved();
        // Keep persistenceReady false in safe mode so schedule/flush paths also no-op early.
        persistenceReadyRef.current = !safeMode;
        syncActiveWorkspaceSession();
        uiStore.setDocumentLoading(false);
      } catch (error) {
        console.warn("Failed to restore persisted document", error);
        if (!cancelled) {
          centerViewportOnDocumentPoint(0, 0);
          uiStore.setMessage(safeMode ? "Safe mode (persistence writes off)" : "New document");
          try {
            await resolveDocumentFonts(latestDocumentRef.current);
            if (!cancelled) {
              notifyFontsResolved();
            }
          } catch (fontError) {
            console.warn("Failed to resolve document fonts", fontError);
          }
          persistenceReadyRef.current = !safeMode;
          syncActiveWorkspaceSession();
          uiStore.setDocumentLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notifyFontsResolved]);

  useEffect(() => {
    if (isDocumentLoading) {
      return;
    }

    let cancelled = false;
    void resolveDocumentFonts(documentStore.getDocument()).then(fontsChanged => {
      if (!cancelled && fontsChanged) {
        notifyFontsResolved();
      }
    }).catch(error => {
      console.warn("Failed to resolve document fonts", error);
    });

    return () => {
      cancelled = true;
    };
  }, [document, isDocumentLoading, notifyFontsResolved]);

  useEffect(() => {
    if (mode !== "vectorEdit" && (activeTool === "fill" || activeTool === "shapeBuilder" || activeTool === "sculpt" || activeTool === "subdivide" || activeTool === "extrude")) {
      if (activeTool === "subdivide") {
        cancelSubdivideSession(false);
      }
      if (activeTool === "extrude") {
        viewStore.setVectorTopologyPreview(undefined);
      }
      selectionStore.setActiveTool("select");
    }
  }, [activeTool, mode]);

  useEffect(() => {
    if (!uiStore.subdivideSession.getValue()) {
      return;
    }
    if (!subdivideSessionMatchesSelection(selectedEdgeIds, primarySelectedNodeId) || mode !== "vectorEdit") {
      cancelSubdivideSession(true);
    }
  }, [mode, primarySelectedNodeId, selectedEdgeIds]);

  useEffect(() => {
    if (rightPanelTab === "dev" && activeTool !== "select") {
      selectionStore.setActiveTool("select");
    }
  }, [rightPanelTab]);

  useEffect(() => {
    if (!allowScripts) {
      stopScriptRealtimePreview();
    }
  }, [allowScripts]);

  useEffect(() => {
    if (!activeAnimationRootId) return;
    const rootExists = animationGraph.nodes.some(node => node.id === activeAnimationRootId && node.kind === "animation");
    if (rootExists) return;
    uiStore.patchAnimation({ activeRootId: undefined });
    uiStore.patchAnimation({ playing: false });
    uiStore.patchAnimation({ timeMs: 0 });
    uiStore.patchAnimation({ loopRegion: undefined });
  }, [activeAnimationRootId, animationGraph]);

  useEffect(() => {
    if (!animationPlaying || !animationPanelOpen) {
      return;
    }
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const delta = now - last;
      last = now;
      const current = uiStore.animation.getValue().timeMs;
      let next = current + delta;
      if (animationLoopRegion && animationLoopRegion.endMs > animationLoopRegion.startMs) {
        const loopStart = Math.max(0, Math.min(animationLoopRegion.startMs, animationDurationMs));
        const loopEnd = Math.max(loopStart, Math.min(animationLoopRegion.endMs, animationDurationMs));
        const loopDuration = loopEnd - loopStart;
        if (loopDuration > 0) {
          if (current < loopStart || current >= loopEnd) {
            next = loopStart;
          } else if (next >= loopEnd) {
            next = loopStart + ((next - loopEnd) % loopDuration);
          }
        }
      }
      if (next >= animationDurationMs) {
        uiStore.patchAnimation({ playing: false, timeMs: animationDurationMs });
      } else {
        uiStore.patchAnimation({ timeMs: next });
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [animationDurationMs, animationLoopRegion, animationPanelOpen, animationPlaying]);

  useEffect(() => {
    const region = uiStore.animation.getValue().loopRegion;
    if (!region || animationDurationMs <= 0) {
      if (region) {
        uiStore.patchAnimation({ loopRegion: undefined });
      }
      return;
    }
    const startMs = Math.max(0, Math.min(region.startMs, animationDurationMs));
    const endMs = Math.max(0, Math.min(region.endMs, animationDurationMs));
    const next = endMs - startMs >= 10 ? { startMs, endMs } : undefined;
    if (next?.startMs !== region.startMs || next?.endMs !== region.endMs || next === undefined) {
      uiStore.patchAnimation({ loopRegion: next });
    }
  }, [animationDurationMs]);

  useEffect(() => {
    schedulePersistedDocument(documentWithCurrentViewport(documentStore.getDocument()));
  }, [document, schedulePersistedDocument]);

  useEffect(() => {
    const flushPersistedDocument = () => {
      if ((workspaceStore.getActiveSession().kind ?? "document") === "editorScripts") {
        if (editorScriptsSaveTimerRef.current !== undefined) {
          window.clearTimeout(editorScriptsSaveTimerRef.current);
          editorScriptsSaveTimerRef.current = undefined;
        }
        editorScriptsStore.syncFromLiveDocument(latestDocumentRef.current);
        void editorScriptsStore.persistCurrent().catch(error => {
          console.warn("Failed to persist editor scripts", error);
        });
        return;
      }

      if (!persistenceReadyRef.current) {
        return;
      }

      if (persistenceSaveTimerRef.current !== undefined) {
        window.clearTimeout(persistenceSaveTimerRef.current);
        persistenceSaveTimerRef.current = undefined;
      }

      void savePersistedDocument(documentWithCurrentViewport(latestDocumentRef.current)).catch(error => {
        console.warn("Failed to persist document", error);
      });
    };

    const handleVisibilityChange = () => {
      if (globalThis.document.visibilityState === "hidden") {
        flushPersistedDocument();
      }
    };

    window.addEventListener("pagehide", flushPersistedDocument);
    globalThis.document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flushPersistedDocument);
      globalThis.document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (persistenceSaveTimerRef.current !== undefined) {
        window.clearTimeout(persistenceSaveTimerRef.current);
        persistenceSaveTimerRef.current = undefined;
      }
      if (editorScriptsSaveTimerRef.current !== undefined) {
        window.clearTimeout(editorScriptsSaveTimerRef.current);
        editorScriptsSaveTimerRef.current = undefined;
      }
    };
  }, []);

  function handleViewportChange(viewport: DocumentViewport): void {
    if (
      latestViewportRef.current.x === viewport.x &&
      latestViewportRef.current.y === viewport.y &&
      latestViewportRef.current.zoom === viewport.zoom
    ) {
      return;
    }

    latestViewportRef.current = { ...viewport };
  }

  function placeNodeInParent(parentId: string, node: SceneNode, worldBounds: Rect): SceneNode {
    return applyNodeLocalPlacementFromWorld(documentStore.getDocument(), parentId, node, worldBounds, node.transform.rotation ?? 0);
  }

  function insertTextAt(point: { x: number; y: number }, fixedBounds?: Rect): void {
    const parentFrameId = topFrameIdAtPoint(documentStore.getDocument(), point, resolvedActivePageId) ?? activePageFrameId;
    if (!parentFrameId) {
      uiStore.setMessage("No frame available");
      return;
    }

    const context = commandContext();
    const parentFrame = findSceneNode(context.document, parentFrameId);
    const count = parentFrame?.type === "frame" ? parentFrame.children.filter(node => node.type === "text").length : findFirstFrameNode(context.document)?.children.filter(node => node.type === "text").length ?? 0;
    const textLayout = fixedBounds ? "FIXED_SIZE" as const : "AUTO_WIDTH" as const;
    const text = {
      ...createDefaultRichText(""),
      textLayout,
    };
    const node = createTextNode(`Text ${count + 1}`, fixedBounds?.x ?? point.x, fixedBounds?.y ?? point.y, "");
    node.text = text;
    if (fixedBounds) {
      node.bounds = { ...fixedBounds };
    } else {
      const measured = textBoundsForLayout(node, text, Math.round(zoom * 10));
      node.bounds = {
        ...measured,
        x: point.x,
        y: point.y,
        width: Math.max(0, measured.width),
      };
    }
    const placed = placeNodeInParent(parentFrameId, node, node.bounds);
    const nextSelection = selectionSnapshot({ selectedNodeIds: [placed.id], primarySelectedNodeId: placed.id, mode: "textEdit", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
    historyRef.current.execute(new InsertNodeCommand(parentFrameId, placed), context, { afterSelection: nextSelection });
    if (placed.type === "text") {
      beginTextEditSession(textEditSessionRef, placed.id, placed.text);
    }
    applySelectionSnapshot(nextSelection);
    selectionStore.setActiveTool("select");
    commit(context.document, `Inserted ${node.name}`);
  }

  function insertFrameTemplate(template: FrameTemplate): void {
    runInsertFrameTemplate(commandHost, template, { getActivePageId: () => resolvedActivePageId });
  }

  function changeTool(tool: ActiveTool): void {
    if (tool !== "subdivide" && uiStore.subdivideSession.getValue()) {
      cancelSubdivideSession(false);
    }
    if (activeTool === "extrude" && tool !== "extrude") {
      viewStore.setVectorTopologyPreview(undefined);
    }
    selectionStore.setActiveTool(tool);
    if (tool === "extrude") {
      uiStore.setMessage("Extrude edges");
    }
    if ((tool === "shape" || tool === "frame" || tool === "pencil" || tool === "text" || (tool === "path" && mode === "textEdit")) && mode !== "select") {
      updateSelection(selectionSnapshot({ mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined }), true);
    }
  }

  function enterSubdivide(): void {
    if (mode !== "vectorEdit" || !primarySelectedNodeId || selectedEdgeIds.length === 0) {
      uiStore.setMessage("Select one or more edges to subdivide");
      return;
    }
    const currentSession = uiStore.subdivideSession.getValue();
    const previousTool = activeTool === "subdivide"
      ? (currentSession?.previousTool ?? "select")
      : activeTool;
    uiStore.openSubdivideSession({
      nodeId: primarySelectedNodeId,
      edgeIds: selectedEdgeIds,
      segments: currentSession?.segments ?? 2,
      connect: false,
      previousTool,
    });
    selectionStore.setActiveTool("subdivide");
    refreshSubdividePreview(false);
    uiStore.setMessage("Subdivide edges");
  }

  function setSubdivideSegments(segments: number): void {
    uiStore.patchSubdivideSession({ segments });
    refreshSubdividePreview();
  }

  function commitSubdivide(): boolean {
    const session = uiStore.subdivideSession.getValue();
    if (!session) {
      return false;
    }
    const preview = viewStore.vectorTopologyPreview.getValue();
    if (!preview || preview.newVertexIds.length === 0) {
      cancelSubdivideSession();
      return true;
    }
    const previousTool = session.previousTool;
    runSubdivideSelectedEdges(commandHost, preview.nodeId, preview.network, preview.newVertexIds);
    uiStore.closeSubdivideSession();
    viewStore.setVectorTopologyPreview(undefined);
    selectionStore.setActiveTool(previousTool);
    uiStore.setMessage(preview.newVertexIds.length > 1 ? "Subdivided edges" : "Subdivided edge");
    return true;
  }

  function changeShapeKind(kind: PrimitiveKind): void {
    selectionStore.setSelectedShapeKind(kind);
    selectionStore.setActiveTool("shape");
    if (mode !== "select") {
      updateSelection(selectionSnapshot({ mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined }), true);
    }
  }

  function setAnimationModeOpen(open: boolean, recordHistory: boolean): void {
    if (animationPanelOpen === open) {
      return;
    }
    if (!open) {
      uiStore.patchAnimation({ armedNodeKind: undefined, vectorTargetNodeId: undefined });
      uiStore.patchAnimation({ playing: false });
      updateSelection(selectionSnapshot({ animationPanelOpen: false }), recordHistory);
      return;
    }
    const rootNodeId = preferredAnimationRootForAnimationMode(documentStore.getDocument(), animationGraph, {
      selectedNodeIds,
      primarySelectedNodeId,
      viewportCenter: viewStore.viewportCenter.getValue(),
      activePageId: resolvedActivePageId,
    });
    if (rootNodeId) {
      activateAnimationRoot(rootNodeId);
      updateSelection(selectionSnapshot({
        animationPanelOpen: true,
        selectedAnimationNodeIds: [rootNodeId],
        primarySelectedAnimationNodeId: rootNodeId,
        selectedAnimationConnectionIds: [],
      }), recordHistory);
      return;
    }
    updateSelection(selectionSnapshot({ animationPanelOpen: true }), recordHistory);
  }

  function captureLiveSnapshot(): LiveEditorSnapshot {
    const active = workspaceStore.getActiveSession();
    return {
      document: documentStore.getDocument(),
      history: historyRef.current,
      selection: selectionStore.getSelection(),
      tool: selectionStore.tool.getValue(),
      viewport: { ...latestViewportRef.current },
      path: active.path,
      kind: active.kind ?? "document",
    };
  }

  function syncActiveWorkspaceSession(): void {
    workspaceStore.parkActive(captureLiveSnapshot());
  }

  /** Write open tab paths + recents to config.ini (desktop only). */
  function persistDesktopWorkspaceSession(options: { flush?: boolean } = {}): void {
    if (!isNativeShell() || isSafeMode()) {
      return;
    }
    syncActiveWorkspaceSession();
    const { sessions, activeSessionId } = workspaceStore.getState();
    const paths = sessions
      .map(session => session.path)
      .filter((path): path is string => Boolean(path));
    const activePath = sessions.find(session => session.id === activeSessionId)?.path;
    const active = activePath ? Math.max(0, paths.indexOf(activePath)) : 0;
    void setDesktopSession(paths, active)
      .then(async () => {
        if (options.flush) {
          await flushDesktopConfig();
        }
        await refreshNativeRecentsMenu();
      })
      .catch(error => {
        console.warn("Failed to persist desktop session", error);
      });
  }

  function applyHydratedSnapshot(snapshot: LiveEditorSnapshot): void {
    hydrateLiveSnapshot(snapshot, {
      setHistory: history => {
        historyRef.current = history;
      },
      setViewport: viewport => {
        latestViewportRef.current = viewport;
      },
      setDocumentRef: nextDocument => {
        latestDocumentRef.current = nextDocument;
      },
      resetScopedState: resetDocumentScopedState,
    });
    // CommandHost reads history via ref; re-assert MCP binding after activate/switch.
    setMcpToolSession({
      commandHost,
      runDocumentScript: (scriptId, options) => runDocumentScriptForMcpRef.current(scriptId, options),
    });
  }

  function activateWorkspaceSession(sessionId: string): void {
    const snapshot = workspaceStore.switchTo(sessionId, captureLiveSnapshot());
    applyHydratedSnapshot(snapshot);
    void resolveDocumentFonts(documentStore.getDocument()).then(fontsChanged => {
      if (fontsChanged) {
        notifyFontsResolved();
      }
    });
    persistDesktopWorkspaceSession();
  }

  function requestCloseWorkspaceSession(sessionId: string): void {
    void requestCloseWorkspaceSessions([sessionId]);
  }

  async function requestCloseWorkspaceSessions(sessionIds: string[]): Promise<void> {
    if (sessionIds.length === 0) {
      return;
    }

    const { sessions, activeSessionId } = workspaceStore.getState();
    const closing = sessions.filter(session => sessionIds.includes(session.id));
    if (closing.length === 0) {
      return;
    }

    const dirtySessions = closing.filter(session => {
      if (session.id === activeSessionId) {
        return hasUnsavedDocumentChanges();
      }
      return session.history.getState(session.document.revision).dirtyRevision > 0;
    });
    if (dirtySessions.length > 0) {
      const message = dirtySessions.length === 1
        ? `"${documentDisplayName(
          dirtySessions[0]!.id === activeSessionId
            ? documentStore.getDocument().name
            : dirtySessions[0]!.document.name,
        )}" has unsaved changes. Close anyway?`
        : `${dirtySessions.length} documents have unsaved changes. Close anyway?`;
      if (!(await confirmDiscard(message))) {
        return;
      }
    }

    const closingActive = sessionIds.includes(activeSessionId);
    const snapshot = workspaceStore.closeSessions(sessionIds, captureLiveSnapshot());
    if (closingActive) {
      applyHydratedSnapshot(snapshot);
      void resolveDocumentFonts(documentStore.getDocument()).then(fontsChanged => {
        if (fontsChanged) {
          notifyFontsResolved();
        }
      });
    }
    persistDesktopWorkspaceSession();
  }

  function renameWorkspaceSession(sessionId: string, name: string): void {
    if (sessionId !== workspaceStore.getState().activeSessionId) {
      activateWorkspaceSession(sessionId);
    }
    renameDocument(name);
  }

  function hasUnsavedDocumentChanges(): boolean {
    return historyRef.current.getState(document.revision).dirtyRevision > 0;
  }

  function syncWindowTitle(): void {
    const live = documentStore.getDocument();
    const dirty = historyRef.current.getState(live.revision).dirtyRevision > 0;
    setWindowTitle(formatWindowTitle(documentDisplayName(live.name), dirty));
  }

  /** Park active, then list dirty sessions (for quit / close-all). */
  function collectDirtyWorkspaceSessions(): Array<{ id: string; name: string }> {
    syncActiveWorkspaceSession();
    return workspaceStore.getState().sessions
      .filter(session => session.history.getState(session.document.revision).dirtyRevision > 0)
      .map(session => ({
        id: session.id,
        name: documentDisplayName(
          session.id === workspaceStore.getState().activeSessionId
            ? documentStore.getDocument().name
            : session.document.name,
        ),
      }));
  }

  function confirmDiscardDirtyWorkspace(actionLabel: string): Promise<boolean> {
    const dirty = collectDirtyWorkspaceSessions();
    if (dirty.length === 0) {
      return Promise.resolve(true);
    }
    const message = dirty.length === 1
      ? `"${dirty[0]!.name}" has unsaved changes. ${actionLabel}?`
      : `${dirty.length} documents have unsaved changes. ${actionLabel}?`;
    return confirmDiscard(message);
  }

  function requestNewDocument(): void {
    // Desktop: park the current tab and open a blank one (no discard).
    if (!isNativeShell()) {
      void (async () => {
        if (hasUnsavedDocumentChanges() && !(await confirmDiscard("You have unsaved changes. Discard them and create a new document?"))) {
          return;
        }
        newDocument();
      })();
      return;
    }
    newDocument();
  }

  function newDocument(): void {
    if (isNativeShell()) {
      const session = createDocumentSession();
      const snapshot = workspaceStore.openSession(session, captureLiveSnapshot());
      applyHydratedSnapshot(snapshot);
      centerViewportOnDocumentPoint(0, 0);
      uiStore.setMessage("New document");
      persistDesktopWorkspaceSession();
      return;
    }

    runNewDocument(commandHost, {
      resetHistory: () => {
        historyRef.current = new CommandHistory();
      },
      setViewport: viewport => {
        latestViewportRef.current = viewport;
      },
      resetScopedState: resetDocumentScopedState,
      centerViewportOnDocumentPoint,
    });
    syncActiveWorkspaceSession();
  }

  async function requestCurvaImport(): Promise<void> {
    const file = await pickOpenFile({ accept: CURVA_FILE_EXTENSION });
    await openCurvaDocument(file);
  }

  async function requestComponentImport(): Promise<void> {
    const file = await pickOpenFile({ accept: CURVA_FILE_EXTENSION });
    await importComponentsFromCurvaFile(file);
  }

  async function requestScriptImport(): Promise<void> {
    const file = await pickOpenFile({ accept: CURVA_FILE_EXTENSION });
    await runImportScriptsFromCurvaFile(commandHost, file);
  }

  async function requestScriptImportAndReplace(): Promise<void> {
    const file = await pickOpenFile({ accept: CURVA_FILE_EXTENSION });
    await runImportAndReplaceScriptsFromCurvaFile(commandHost, file);
  }

  async function requestAddEditorScripts(): Promise<void> {
    const file = await pickOpenFile({ accept: CURVA_FILE_EXTENSION });
    await runAddEditorScriptsFromCurvaFile(commandHost, file);
    refreshOpenEditorScriptsSessionFromStore();
  }

  async function requestReplaceEditorScripts(): Promise<void> {
    const file = await pickOpenFile({ accept: CURVA_FILE_EXTENSION });
    await runReplaceEditorScriptsFromCurvaFile(commandHost, file);
    refreshOpenEditorScriptsSessionFromStore();
  }

  function clearEditorScriptsLibrary(): void {
    runClearEditorScripts(commandHost);
    refreshOpenEditorScriptsSessionFromStore();
  }

  function refreshOpenEditorScriptsSessionFromStore(): void {
    if ((workspaceStore.getActiveSession().kind ?? "document") !== "editorScripts") {
      return;
    }
    const next = editorScriptsStore.getDocument() ?? createEmptyEditorScriptsDocument();
    historyRef.current = new CommandHistory();
    historyRef.current.markSaved(next.revision);
    latestDocumentRef.current = next;
    documentStore.setDocument(next);
    syncActiveWorkspaceSession();
  }

  async function openEditorScriptsDocument(): Promise<void> {
    const existing = workspaceStore.findSessionByKind("editorScripts");
    if (existing) {
      activateWorkspaceSession(existing.id);
      uiStore.setMessage(`Switched to ${EDITOR_SCRIPTS_DOCUMENT_NAME}`);
      return;
    }

    await editorScriptsStore.bootstrap();
    const document = editorScriptsStore.getDocument() ?? createEmptyEditorScriptsDocument();
    editorScriptsStore.setDocument(document);
    const path = await editorScriptsSessionPath();
    const session = createDocumentSession({
      document,
      path,
      kind: "editorScripts",
      viewport: { ...DEFAULT_DOCUMENT_VIEWPORT, ...document.viewport },
    });
    const snapshot = workspaceStore.openSession(session, captureLiveSnapshot());
    applyHydratedSnapshot(snapshot);
    await resolveDocumentFonts(documentStore.getDocument());
    notifyFontsResolved();
    uiStore.setMessage(`Opened ${EDITOR_SCRIPTS_DOCUMENT_NAME}`);
    persistDesktopWorkspaceSession();
  }

  async function downloadEditorScriptsDocumentCopy(): Promise<void> {
    await editorScriptsStore.bootstrap();
    const live = (workspaceStore.getActiveSession().kind ?? "document") === "editorScripts"
      ? documentStore.getDocument()
      : undefined;
    const document = live
      ?? editorScriptsStore.getDocument()
      ?? createEmptyEditorScriptsDocument();
    await runDownloadEditorScriptsDocument(commandHost, document);
  }

  async function openCurvaDocument(file: OpenedFile | undefined): Promise<void> {
    if (!file) {
      return;
    }

    if (file.path) {
      const existing = workspaceStore.findSessionByPath(file.path);
      if (existing) {
        activateWorkspaceSession(existing.id);
        uiStore.setMessage(`Switched to ${existing.document.name}`);
        return;
      }
    }

    try {
      uiStore.setDocumentLoading(true);
      const imported = deserializeCurvaPackage(file.bytes).document;
      const nextDocument = {
        ...imported,
        name: sanitizeDocumentBaseName(file.name.endsWith(CURVA_FILE_EXTENSION) ? file.name : imported.name),
      };
      const session = createDocumentSession({
        document: nextDocument,
        path: file.path,
        viewport: { ...DEFAULT_DOCUMENT_VIEWPORT, ...nextDocument.viewport },
      });

      const workspace = workspaceStore.getState();
      const onlyBlankNative = isNativeShell()
        && workspace.sessions.length === 1
        && !workspace.sessions[0]?.path;
      const snapshot = isNativeShell()
        ? (onlyBlankNative
          ? workspaceStore.replaceActive(session, captureLiveSnapshot())
          : workspaceStore.openSession(session, captureLiveSnapshot()))
        : workspaceStore.replaceActive(session, captureLiveSnapshot());
      applyHydratedSnapshot(snapshot);
      await resolveDocumentFonts(documentStore.getDocument());
      notifyFontsResolved();
      uiStore.setMessage(isNativeShell() ? `Opened ${file.name}` : `Imported ${file.name}`);
      if (file.path) {
        persistDesktopWorkspaceSession();
      }
    } catch (error) {
      uiStore.setMessage(error instanceof Error ? error.message : "Could not open .curva file");
    } finally {
      uiStore.setDocumentLoading(false);
    }
  }

  /** @deprecated Prefer `openCurvaDocument` — kept for any remaining call sites. */
  async function importCurvaFile(file: OpenedFile | undefined): Promise<void> {
    await openCurvaDocument(file);
  }

  async function importComponentsFromCurvaFile(file: OpenedFile | undefined): Promise<void> {
    await runImportComponentsFromCurvaFile(commandHost, file, {
      componentImportInProgress: componentImportProgress !== undefined,
      centerViewportOnDocumentPoint,
      refreshComponentThumbnail,
    });
  }

  function renameDocument(name: string): void {
    const nextName = sanitizeDocumentBaseName(name);
    if (nextName === sanitizeDocumentBaseName(document.name)) {
      return;
    }

    const context = commandContext();
    historyRef.current.execute(new RenameDocumentCommand(document.name, nextName), context, { afterSelection: currentSelection() });
    commit(context.document, "Renamed document");
  }

  async function exportCurvaFile(): Promise<void> {
    const active = workspaceStore.getActiveSession();
    const savedPath = await runExportCurvaFile(commandHost, {
      path: active.path,
    });
    if (savedPath) {
      workspaceStore.setActivePath(savedPath);
      syncActiveWorkspaceSession();
      persistDesktopWorkspaceSession();
    } else {
      // Browser download or cancelled — still park history dirty state after markSaved.
      syncActiveWorkspaceSession();
    }
    if ((active.kind ?? "document") === "editorScripts") {
      editorScriptsStore.syncFromLiveDocument(documentStore.getDocument());
      void editorScriptsStore.persistCurrent().catch(error => {
        console.warn("Failed to persist editor scripts after save", error);
      });
    }
    syncWindowTitle();
  }

  async function exportCurvaFileAs(): Promise<void> {
    const savedPath = await runExportCurvaFile(commandHost, { path: undefined });
    if (savedPath) {
      workspaceStore.setActivePath(savedPath);
      syncActiveWorkspaceSession();
      persistDesktopWorkspaceSession();
    }
    if ((workspaceStore.getActiveSession().kind ?? "document") === "editorScripts") {
      editorScriptsStore.syncFromLiveDocument(documentStore.getDocument());
      void editorScriptsStore.persistCurrent().catch(error => {
        console.warn("Failed to persist editor scripts after save", error);
      });
    }
    syncWindowTitle();
  }

  async function exportSelectedFramesPdf(): Promise<void> {
    await runExportSelectedFramesPdf(commandHost);
  }

  function selectNodes(nodeIds: string[], primaryNodeId = nodeIds.at(-1), options: { recordHistory?: boolean } = {}): void {
    runSelectNodes(commandHost, nodeIds, primaryNodeId, options);
  }

  function editableSelection(nodeIds = selectedNodeIds): string[] {
    return runEditableSelection(nodeIds);
  }

  function updateNodeBounds(
    updates: Array<{ nodeId: string; bounds: Rect }>,
    label = "Updated bounds",
    options: { dropPoint?: { x: number; y: number }; ignoreChildConstraints?: boolean } = {},
  ): void {
    runUpdateNodeBounds(commandHost, updates, label, options);
  }

  function updateSelectionBounds(toBounds: Rect): void {
    runUpdateSelectionBounds(commandHost, selectedNodeIds, selectionBounds, toBounds);
  }

  function updateNodeTransforms(updates: Array<{ nodeId: string; transform: SceneNode["transform"] }>, label = "Updated transform"): void {
    runUpdateNodeTransforms(commandHost, updates, label);
  }

  function updateSelectionRotation(rotation: number): void {
    const nodes = getAllSceneNodes(documentStore.getDocument()).filter(node => selectedNodeIds.includes(node.id) && !isNodeLockedForEditing(documentStore.getDocument(), node.id));
    updateNodeTransforms(
      nodes.map(node => ({
        nodeId: node.id,
        transform: rotationTransformForNode(node, rotation),
      })),
      "Updated rotation",
    );
  }

  function updateRotationOrigin(nodeId: string, origin: { x: number; y: number }): void {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
    if (!node || isNodeLockedForEditing(documentStore.getDocument(), node.id)) {
      return;
    }

    const currentOrigin = transformRotationOrigin(node.bounds, node.transform);
    if (Math.hypot(currentOrigin.x - origin.x, currentOrigin.y - origin.y) <= 0.001) {
      return;
    }

    const commands: Command[] = [];
    if (node.transform.rotation !== 0) {
      const compensatedOrigin = inverseRotatePoint(currentOrigin, origin, node.transform.rotation);
      const dx = compensatedOrigin.x - currentOrigin.x;
      const dy = compensatedOrigin.y - currentOrigin.y;
      const nextBounds = { ...node.bounds, x: node.bounds.x + dx, y: node.bounds.y + dy };
      if (!boundsEqual(node.bounds, nextBounds)) {
        commands.push(new UpdateNodeBoundsCommand(node.id, node.bounds, nextBounds));
      }
    }

    commands.push(new UpdateNodeTransformCommand(node.id, node.transform, { ...node.transform, x: origin.x, y: origin.y }));
    const context = commandContext();
    historyRef.current.execute(
      commands.length === 1 ? commands[0]! : new TransactionCommand("Updated rotation origin", commands),
      context,
      { afterSelection: currentSelection() },
    );
    commit(context.document, "Updated rotation origin");
  }

  function rotationTransformForNode(node: SceneNode, rotation: number): SceneNode["transform"] {
    const needsInitialOrigin = node.transform.rotation === 0 && node.transform.x === 0 && node.transform.y === 0;
    return {
      ...node.transform,
      ...(needsInitialOrigin ? rectCenter(node.bounds) : {}),
      rotation,
    };
  }

  function alignSelectedNodes(action: AlignmentAction): void {
    runAlignSelectedNodes(commandHost, action, selectionBounds);
  }

  function flipSelectedNodes(axis: "horizontal" | "vertical"): void {
    runFlipSelectedNodes(commandHost, axis, selectionBounds);
  }

  function updateNodeStyle(nodeId: string, style: NodeStyle): void {
    runUpdateNodeStyle(commandHost, nodeId, style);
  }

  function updateSelectedNodeStyles(updates: Array<{ nodeId: string; style: NodeStyle }>, label: string): void {
    runUpdateSelectedNodeStyles(commandHost, updates, label);
  }

  function openSelectionPaint(kind: SelectionPaintKind): void {
    const editableNodeIds = runEnsureSelectionPaint(commandHost, selectedNodeIds, kind);
    if (editableNodeIds.length === 0) {
      uiStore.setMessage(`Select an editable shape to change its ${kind}`);
      return;
    }

    const pickerPrimaryId = primarySelectedNodeId && editableNodeIds.includes(primarySelectedNodeId)
      ? primarySelectedNodeId
      : editableNodeIds[0];
    uiStore.openSelectionPaintPicker(kind, editableNodeIds, selectedNodeIds, pickerPrimaryId);
  }

  function updateSelectionPaint(nodeIds: string[], kind: SelectionPaintKind, paint: Paint): void {
    runUpdateSelectionPaint(
      commandHost,
      nodeIds,
      kind,
      paint,
      nodeIds.length > 1 ? `Updated ${kind} on ${nodeIds.length} shapes` : `Updated ${kind}`,
    );
  }

  function updatePrimitiveDefinition(nodeId: string, primitive: PrimitiveDefinition): void {
    runUpdatePrimitiveDefinition(commandHost, nodeId, primitive);
  }

  function updateTextNode(nodeId: string, text: RichTextModel, message = "Updated text"): void {
    runUpdateTextNode(commandHost, nodeId, text, message);
  }

  function updateTextCharacters(nodeId: string, characters: string): void {
    runUpdateTextCharacters(commandHost, nodeId, characters);
  }

  function toggleTextBold(nodeId: string, selectionStart: number, selectionEnd: number): void {
    runToggleTextBold(commandHost, nodeId, selectionStart, selectionEnd);
  }

  function updateTextTypeStyle(nodeId: string, patch: Partial<TypeStyle>): void {
    runUpdateTextTypeStyle(commandHost, nodeId, patch, textHandlerDeps);
  }

  function previewTextTypeStyle(nodeId: string, patch: Partial<TypeStyle>): void {
    runPreviewTextTypeStyle(nodeId, patch, textHandlerDeps);
  }

  function clearTextTypeStylePreview(nodeId?: string): void {
    runClearTextTypeStylePreview(nodeId, textHandlerDeps);
  }

  function updateTextSelectionState(selection: { nodeId: string; anchor: number; focus: number; focused: boolean }): void {
    uiStore.setTextSelection(selection);
  }

  function textFillForInspector(nodeId: string): Paint | "mixed" | undefined {
    return runTextFillForInspector(nodeId);
  }

  function updateFrameClipsContent(nodeId: string, clipsContent: boolean): void {
    runUpdateFrameClipsContent(commandHost, nodeId, clipsContent);
  }

  function updateDocumentScriptSource(scriptId: string, source: string): void {
    runUpdateDocumentScriptSource(commandHost, scriptId, source);
  }

  function createDocumentScript(parentFolderId?: string): string | undefined {
    if (!uiStore.allowScripts.getValue()) {
      return undefined;
    }
    return runCreateDocumentScript(commandHost, undefined, parentFolderId);
  }

  function createScriptFolder(): string | undefined {
    if (!uiStore.allowScripts.getValue()) {
      return undefined;
    }
    return runCreateScriptFolder(commandHost);
  }

  function renameScriptEntry(entryId: string, name: string): void {
    runRenameScriptEntry(commandHost, entryId, name);
  }

  function duplicateDocumentScript(scriptId: string): string | undefined {
    return runDuplicateDocumentScript(commandHost, scriptId);
  }

  function deleteScriptEntry(entryId: string): void {
    runDeleteScriptEntry(commandHost, entryId);
  }

  function moveScriptEntry(entryId: string, parentFolderId: string | undefined, index: number): void {
    runMoveScriptEntry(commandHost, entryId, parentFolderId, index);
  }

  function setFrameScriptId(frameId: string, scriptId: string | undefined): void {
    runSetFrameScriptId(commandHost, frameId, scriptId);
  }

  function openAttachedFrameScriptEditor(frameId: string): void {
    const liveDocument = documentStore.getDocument();
    const node = findSceneNode(liveDocument, frameId);
    if (!frameHasScript(liveDocument, node)) {
      return;
    }
    uiStore.openScriptEditor(node.scriptId);
  }

  function openScriptAttachPicker(frameId: string): void {
    if (!uiStore.allowScripts.getValue()) {
      return;
    }
    const node = findSceneNode(documentStore.getDocument(), frameId);
    if (node?.type !== "frame") {
      return;
    }
    setScriptAttachFrameId(frameId);
    uiStore.setContextMenu(undefined);
  }

  function detachFrameScript(frameId: string): void {
    const liveDocument = documentStore.getDocument();
    const node = findSceneNode(liveDocument, frameId);
    if (!frameHasScript(liveDocument, node)) {
      return;
    }
    setFrameScriptId(frameId, undefined);
    uiStore.setContextMenu(undefined);
  }

  function createAndAttachScript(frameId: string): void {
    const scriptId = createDocumentScript();
    if (!scriptId) {
      return;
    }
    setFrameScriptId(frameId, scriptId);
    setScriptAttachFrameId(undefined);
    uiStore.openScriptEditor(scriptId);
  }

  function applyScriptLogDelivery(delivery: ScriptLogDelivery): void {
    const source = delivery.meta?.frameName ?? delivery.meta?.frameId;
    const entries: Array<{ level: "log" | "error" | "info"; message: string; source?: string }> =
      delivery.entries.map(entry => ({
        level: entry.level,
        message: entry.message,
        source,
      }));
    if (
      delivery.error
      && !entries.some(entry => entry.level === "error" && entry.message === delivery.error)
    ) {
      entries.push({
        level: "error",
        message: delivery.error,
        source,
      });
    }
    uiStore.appendScriptDebugLogs(entries);
  }

  function stopScriptRealtimePreview(): void {
    const controller = scriptRealtimeRef.current;
    scriptRealtimeRef.current = undefined;
    scriptPreviewInputRef.current.reset();
    if (controller) {
      controller.stop();
      return;
    }
    uiStore.clearScriptPreview();
    // Sync-only: an async stopSession raced with startSession for realtime scripts.
    // If the engine is not loaded yet, there is no session to stop.
    stopAttachedFrameScriptSession();
  }

  function parseScriptPagesJson(pagesJson: string, fallback: CurvaDocument["pages"]): CurvaDocument["pages"] {
    try {
      return JSON.parse(pagesJson) as CurvaDocument["pages"];
    } catch (error) {
      console.warn("[script] failed to parse session pages", error);
      return fallback;
    }
  }

  async function runDocumentScript(
    scriptId: string,
    options?: {
      contextFrameId?: string;
      paramValues?: Record<string, ScriptParamValue>;
      /** When false, skip the params modal and inject defaults + paramValues. Default true. */
      interactive?: boolean;
      scriptLibrarySource?: "document" | "editor";
    },
  ): Promise<McpRunScriptResult | undefined> {
    if (!uiStore.allowScripts.getValue()) {
      return {
        ok: false,
        scriptId,
        scriptName: "",
        logs: [],
        applied: false,
        realtime: false,
        message: "Scripts are disabled",
        error: "Scripts are disabled. Enable Allow Scripts in the editor (View menu).",
      };
    }
    const librarySource = options?.scriptLibrarySource ?? "document";
    const liveDocument = documentStore.getDocument();
    const script = findDocumentScript(
      librarySource === "editor" ? editorScriptsStore.getLibrary() : liveDocument.scripts,
      scriptId,
    );
    if (!script) {
      return {
        ok: false,
        scriptId,
        scriptName: "",
        logs: [],
        applied: false,
        realtime: false,
        message: "Script not found",
        error: `Script "${scriptId}" was not found.`,
      };
    }

    const contextFrame = resolveScriptRunContextFrame(liveDocument, options);
    if (!contextFrame) {
      const message = "Select a frame, or center a top-level frame in the viewport, to run this script.";
      uiStore.appendScriptDebugLogs([{ level: "error", message, source: script.name }]);
      uiStore.setMessage(message);
      return {
        ok: false,
        scriptId: script.id,
        scriptName: script.name,
        logs: [message],
        applied: false,
        realtime: false,
        message,
        error: message,
      };
    }

    const interactive = options?.interactive !== false;
    const params = parseScriptParams(script.source);
    if (interactive && params.length > 0 && options?.paramValues === undefined) {
      setScriptParamsSession(createScriptParamsSession({
        scriptId,
        scriptName: script.name,
        scriptLibrarySource: librarySource,
        contextFrameId: options?.contextFrameId ?? contextFrame.id,
        params,
        source: script.source,
      }));
      return undefined;
    }

    const values = {
      ...defaultValuesForParams(params),
      ...(options?.paramValues ?? {}),
    };
    const source = params.length > 0
      ? applyScriptParamValues(script.source, values)
      : script.source;
    return executeDocumentScript(script, contextFrame, source);
  }

  function runEditorScript(
    scriptId: string,
    options?: {
      contextFrameId?: string;
      paramValues?: Record<string, ScriptParamValue>;
      interactive?: boolean;
    },
  ): Promise<McpRunScriptResult | undefined> {
    return runDocumentScript(scriptId, {
      ...options,
      scriptLibrarySource: "editor",
    });
  }

  runDocumentScriptForMcpRef.current = async (scriptId, options) => {
    const result = await runDocumentScript(scriptId, {
      contextFrameId: options?.contextFrameId,
      paramValues: options?.values,
      interactive: false,
    });
    return result ?? {
      ok: false,
      scriptId,
      scriptName: "",
      logs: [],
      applied: false,
      realtime: false,
      message: "Script run did not start",
      error: "Script run did not start",
    };
  };

  async function confirmScriptParamsRun(): Promise<void> {
    const session = scriptParamsSession;
    if (!session) {
      return;
    }

    // Invalidate param live-preview without closing the modal yet — clearing
    // scriptParamsSession mid-await used to race an async stopSession against
    // realtime scripts that keep a Wasm session after init.
    scriptParamPreviewGenRef.current += 1;
    if (scriptParamPreviewTimerRef.current !== undefined) {
      clearTimeout(scriptParamPreviewTimerRef.current);
      scriptParamPreviewTimerRef.current = undefined;
    }
    uiStore.clearScriptParamPreview();
    stopAttachedFrameScriptSession();

    const liveDocument = documentStore.getDocument();
    const script = findDocumentScript(
      session.scriptLibrarySource === "editor"
        ? editorScriptsStore.getLibrary()
        : liveDocument.scripts,
      session.scriptId,
    );
    if (!script) {
      setScriptParamsSession(undefined);
      return;
    }

    const contextFrame = resolveScriptRunContextFrame(liveDocument, {
      contextFrameId: session.contextFrameId,
    });
    if (!contextFrame) {
      setScriptParamsSession(undefined);
      const message = "Select a frame, or center a top-level frame in the viewport, to run this script.";
      uiStore.appendScriptDebugLogs([{ level: "error", message, source: script.name }]);
      uiStore.setMessage(message);
      return;
    }

    const source = applyScriptParamValues(script.source, session.values);
    await executeDocumentScript(script, contextFrame, source);
    setScriptParamsSession(undefined);
  }

  function clearScriptParamLivePreview(): void {
    scriptParamPreviewGenRef.current += 1;
    if (scriptParamPreviewTimerRef.current !== undefined) {
      clearTimeout(scriptParamPreviewTimerRef.current);
      scriptParamPreviewTimerRef.current = undefined;
    }
    uiStore.clearScriptParamPreview();
    if (!scriptRealtimeRef.current) {
      stopAttachedFrameScriptSession();
    }
  }

  async function runScriptParamLivePreview(session: ScriptParamsModalSession): Promise<void> {
    if (!session.livePreview || !session.supportsLivePreview) {
      return;
    }
    if (scriptRealtimeRef.current) {
      return;
    }

    const generation = ++scriptParamPreviewGenRef.current;
    const liveDocument = documentStore.getDocument();
    const script = findDocumentScript(
      session.scriptLibrarySource === "editor"
        ? editorScriptsStore.getLibrary()
        : liveDocument.scripts,
      session.scriptId,
    );
    if (!script) {
      return;
    }

    const contextFrame = resolveScriptRunContextFrame(liveDocument, {
      contextFrameId: session.contextFrameId,
    });
    if (!contextFrame) {
      return;
    }

    const engine = await ensureDefaultScriptEngine();
    if (!engine || generation !== scriptParamPreviewGenRef.current || scriptRealtimeRef.current) {
      return;
    }

    const workingPages = structuredClone(liveDocument.pages);
    const source = applyScriptParamValues(script.source, session.values);
    const runId = `script-param-preview-${script.id}-${contextFrame.id}-${generation}`;
    const selectionNow = selectionStore.selection.getValue();

    const publishPages = (pages: CurvaDocument["pages"]) => {
      if (generation !== scriptParamPreviewGenRef.current) {
        return;
      }
      const current = uiStore.scriptParamPreview.getValue();
      if (current?.frameId === contextFrame.id) {
        uiStore.updateScriptParamPreviewPages(pages);
        return;
      }
      uiStore.openScriptParamPreview({
        frameId: contextFrame.id,
        frameName: contextFrame.name || undefined,
        pages,
      });
    };

    const outcome = await startAttachedFrameScript(source, {
      engine,
      meta: { frameId: contextFrame.id, frameName: contextFrame.name || undefined },
      context: {
        runId,
        frameId: contextFrame.id,
        pages: workingPages,
        selectedNodeIds: [...selectionNow.selectedNodeIds],
        primarySelectedNodeId: selectionNow.primarySelectedNodeId,
      },
      onApplyPages: (_applyRunId, pagesJson) => {
        publishPages(parseScriptPagesJson(pagesJson, workingPages));
      },
    });

    if (generation !== scriptParamPreviewGenRef.current) {
      return;
    }

    if (outcome?.sessionAlive && (outcome.hasUpdate || outcome.hasDraw)) {
      stopAttachedFrameScriptSession(engine);
      uiStore.clearScriptParamPreview();
      setScriptParamsSession(current => (
        current && current.scriptId === session.scriptId
          ? { ...current, supportsLivePreview: false, livePreview: false }
          : current
      ));
      return;
    }

    stopAttachedFrameScriptSession(engine);

    const pages = outcome?.pagesJson
      ? parseScriptPagesJson(outcome.pagesJson, workingPages)
      : workingPages;
    publishPages(pages);
  }

  useEffect(() => {
    if (!scriptParamsSession) {
      // Clear param-preview UI only — do not stop the Wasm session here.
      // confirmScriptParamsRun closes the modal after starting realtime scripts;
      // an async stopSession from this effect was killing those sessions.
      scriptParamPreviewGenRef.current += 1;
      if (scriptParamPreviewTimerRef.current !== undefined) {
        clearTimeout(scriptParamPreviewTimerRef.current);
        scriptParamPreviewTimerRef.current = undefined;
      }
      uiStore.clearScriptParamPreview();
      return;
    }
    if (!scriptParamsSession.livePreview || !scriptParamsSession.supportsLivePreview) {
      scriptParamPreviewGenRef.current += 1;
      if (scriptParamPreviewTimerRef.current !== undefined) {
        clearTimeout(scriptParamPreviewTimerRef.current);
        scriptParamPreviewTimerRef.current = undefined;
      }
      uiStore.clearScriptParamPreview();
      return;
    }

    const session = scriptParamsSession;
    // Invalidate any in-flight preview while debounce waits for the latest values.
    scriptParamPreviewGenRef.current += 1;
    if (scriptParamPreviewTimerRef.current !== undefined) {
      clearTimeout(scriptParamPreviewTimerRef.current);
    }
    scriptParamPreviewTimerRef.current = setTimeout(() => {
      scriptParamPreviewTimerRef.current = undefined;
      void runScriptParamLivePreview(session);
    }, 180);

    return () => {
      if (scriptParamPreviewTimerRef.current !== undefined) {
        clearTimeout(scriptParamPreviewTimerRef.current);
        scriptParamPreviewTimerRef.current = undefined;
      }
    };
  }, [
    scriptParamsSession,
    scriptParamsSession?.scriptId,
    scriptParamsSession?.contextFrameId,
    scriptParamsSession?.livePreview,
    scriptParamsSession?.supportsLivePreview,
    scriptParamsSession?.values,
  ]);

  function resolveScriptRunContextFrame(
    liveDocument: CurvaDocument,
    options?: { contextFrameId?: string },
  ): FrameNode | undefined {
    if (options?.contextFrameId) {
      const node = findSceneNode(liveDocument, options.contextFrameId);
      return node?.type === "frame" ? node : undefined;
    }
    return resolveScriptContextFrameFromSelection(liveDocument, {
      selectedNodeIds,
      primarySelectedNodeId,
      viewportCenter: viewStore.viewportCenter.getValue(),
      activePageId: resolvedActivePageId,
    });
  }

  async function executeDocumentScript(
    script: { id: string; name: string },
    contextFrame: FrameNode,
    source: string,
  ): Promise<McpRunScriptResult> {
    const liveDocument = documentStore.getDocument();
    const frameId = contextFrame.id;
    const runLogs: string[] = [];
    stopScriptRealtimePreview();

    uiStore.appendScriptDebugLogs([{
      level: "log",
      message: "Running script…",
      source: script.name,
    }]);

    const captureLogs = (delivery: ScriptLogDelivery) => {
      applyScriptLogDelivery(delivery);
      for (const entry of delivery.entries) {
        runLogs.push(entry.message);
      }
      if (delivery.error && !delivery.entries.some(entry => entry.message === delivery.error)) {
        runLogs.push(delivery.error);
      }
    };

    const engine = await ensureDefaultScriptEngine();
    if (!engine) {
      const message = "Script engine unavailable. Run `bun run build:curva-script`.";
      uiStore.appendScriptDebugLogs([{ level: "error", message, source: script.name }]);
      uiStore.setMessage(message);
      return {
        ok: false,
        scriptId: script.id,
        scriptName: script.name,
        contextFrameId: frameId,
        logs: [message],
        applied: false,
        realtime: false,
        message,
        error: message,
      };
    }

    const runId = `script-${script.id}-${frameId}-${Date.now()}`;
    const workingPages = structuredClone(liveDocument.pages);
    const onApplyPages = (applyRunId: string, pagesJson: string) => {
      try {
        const pages = JSON.parse(pagesJson) as typeof workingPages;
        applyScriptDocumentPages(commandHost, applyRunId, pages, frameId);
      } catch (error) {
        console.warn("[script] apply_changes failed", error);
        const message = error instanceof Error ? error.message : "apply_changes failed";
        uiStore.appendScriptDebugLogs([{
          level: "error",
          message,
          source: script.name,
        }]);
        runLogs.push(message);
      }
    };

    const outcome = await startAttachedFrameScript(source, {
      engine,
      meta: { frameId, frameName: contextFrame.name || undefined },
      context: {
        runId,
        frameId,
        pages: workingPages,
        selectedNodeIds: [...selectedNodeIds],
        primarySelectedNodeId: selection.primarySelectedNodeId,
      },
      onApplyPages,
      onLogs: captureLogs,
    });

    if (outcome?.error) {
      uiStore.setMessage(outcome.error);
      return {
        ok: false,
        scriptId: script.id,
        scriptName: script.name,
        contextFrameId: frameId,
        logs: runLogs,
        applied: false,
        realtime: false,
        message: outcome.error,
        error: outcome.error,
      };
    }

    if (outcome?.sessionAlive && (outcome.hasUpdate || outcome.hasDraw)) {
      const pages = outcome.pagesJson
        ? parseScriptPagesJson(outcome.pagesJson, workingPages)
        : workingPages;
      uiStore.openScriptPreview({
        frameId,
        frameName: contextFrame.name || undefined,
        pages,
      });
      const message = `Script running — ${script.name}`;
      uiStore.setMessage(message);

      engine.setApplyHandler?.(onApplyPages);

      scriptPreviewInputRef.current.reset();
      const controller = new ScriptRealtimeController({
        engine,
        meta: { frameId, frameName: contextFrame.name || undefined },
        getInputJson: () => scriptPreviewInputRef.current.toJson(),
        onAfterTick: () => {
          scriptPreviewInputRef.current.clearEdges();
        },
        onPages: pagesJson => {
          uiStore.updateScriptPreviewPages(parseScriptPagesJson(pagesJson, uiStore.scriptPreview.getValue()?.pages ?? workingPages));
        },
        onLogs: captureLogs,
        onError: error => {
          uiStore.setMessage(error);
        },
        onExit: () => {
          uiStore.setMessage(`Script exited — ${script.name}`);
        },
        onStop: () => {
          if (scriptRealtimeRef.current === controller) {
            scriptRealtimeRef.current = undefined;
          }
          scriptPreviewInputRef.current.reset();
          uiStore.clearScriptPreview();
        },
      });
      scriptRealtimeRef.current = controller;
      controller.start();
      return {
        ok: true,
        scriptId: script.id,
        scriptName: script.name,
        contextFrameId: frameId,
        logs: runLogs,
        applied: outcome.applied,
        realtime: true,
        message,
      };
    }

    const message = outcome?.applied
      ? `Script applied — ${script.name}`
      : `Script finished — ${script.name} (no apply_changes)`;
    uiStore.setMessage(message);
    return {
      ok: true,
      scriptId: script.id,
      scriptName: script.name,
      contextFrameId: frameId,
      logs: runLogs,
      applied: Boolean(outcome?.applied),
      realtime: false,
      message,
    };
  }

  async function runFrameAttachedScript(frameId: string): Promise<void> {
    const liveDocument = documentStore.getDocument();
    const node = findSceneNode(liveDocument, frameId);
    if (!frameHasScript(liveDocument, node)) {
      return;
    }
    await runDocumentScript(node.scriptId, { contextFrameId: frameId });
  }

  function updateFrameCornerRadii(nodeId: string, cornerRadii: [number, number, number, number]): void {
    runUpdateFrameCornerRadii(commandHost, nodeId, cornerRadii);
  }

  function updateFrameLayout(nodeId: string, patch: {
    autoLayout?: AutoLayoutMode;
    flexLayout?: FrameFlexLayout;
    repeatLayout?: FrameRepeatLayout;
    mirrorLayout?: FrameMirrorLayout;
  }): void {
    runUpdateFrameLayout(commandHost, nodeId, patch);
  }

  function updateFlexChildLayout(nodeId: string, layout: FlexChildLayout): void {
    runUpdateFlexChildLayout(commandHost, nodeId, layout);
  }

  function updateNodeConstraints(nodeId: string, constraints: NodeConstraints): void {
    runUpdateNodeConstraints(commandHost, nodeId, constraints);
  }

  function switchPage(pageId: string): void {
    runSwitchPage(commandHost, pageId);
  }

  function addPage(): void {
    runAddPage(commandHost);
  }

  function renamePage(pageId: string, name: string): void {
    runRenamePage(commandHost, pageId, name);
  }

  function deletePage(pageId: string): void {
    runDeletePage(commandHost, pageId);
  }

  function duplicatePage(pageId: string): void {
    runDuplicatePage(commandHost, pageId);
  }

  function renameNode(nodeId: string, name: string): void {
    const nextName = name.trim();
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
    if (!node || nextName.length === 0 || node.name === nextName) {
      return;
    }

    const context = commandContext();
    historyRef.current.execute(
      new RenameNodeCommand(nodeId, node.name, nextName, node.nameManuallyEdited === true, true),
      context,
      { afterSelection: currentSelection() },
    );
    commit(context.document, "Renamed layer");
  }

  function updateNodeVisibility(nodeId: string, visible: boolean): void {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
    if (!node || node.visible === visible) {
      return;
    }

    const context = commandContext();
    historyRef.current.execute(
      new UpdateNodeStateCommand(nodeId, { visible: node.visible }, { visible }),
      context,
      { afterSelection: currentSelection() },
    );
    commit(context.document, visible ? "Showed layer" : "Hid layer");
  }

  function updateNodeLocked(nodeId: string, locked: boolean): void {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
    if (!node || node.locked === locked) {
      return;
    }

    const context = commandContext();
    const nextSelection = locked
      ? selectionSnapshot({
          selectedNodeIds: selectedNodeIds.filter(id => id !== nodeId && !isNodeDescendantOf(documentStore.getDocument(), id, nodeId)),
          primarySelectedNodeId: primarySelectedNodeId === nodeId || (primarySelectedNodeId && isNodeDescendantOf(documentStore.getDocument(), primarySelectedNodeId, nodeId))
            ? undefined
            : primarySelectedNodeId,
          mode: "select",
          selectedVertexId: undefined,
          selectedEdgeIds: [],
          selectedHandle: undefined,
        })
      : currentSelection();
    historyRef.current.execute(
      new UpdateNodeStateCommand(nodeId, { locked: node.locked }, { locked }),
      context,
      { afterSelection: nextSelection },
    );
    if (locked) {
      applySelectionSnapshot(nextSelection);
    }
    commit(context.document, locked ? "Locked layer" : "Unlocked layer");
  }

  function toggleNodeMask(nodeId: string): void {
    const node = findSceneNode(documentStore.getDocument(), nodeId);
    if (!node) {
      uiStore.setMessage("Nothing to mask");
      uiStore.setContextMenu(undefined);
      return;
    }

    const nextIsMask = node.isMask !== true;
    const context = commandContext();
    const nextSelection = selectionSnapshot({
      selectedNodeIds: [nodeId],
      primarySelectedNodeId: nodeId,
      mode: "select",
      selectedVertexId: undefined,
      selectedEdgeIds: [],
      selectedHandle: undefined,
    });
    historyRef.current.execute(new UpdateNodeMaskCommand(nodeId, nextIsMask), context, { afterSelection: nextSelection });
    applySelectionSnapshot(nextSelection);
    commit(context.document, nextIsMask ? "Using layer as mask" : "Removed mask");
    uiStore.setContextMenu(undefined);
  }

  function moveNodesInLayerTree(nodeIds: string[], target: LayerDropTarget): void {
    if (isComponentInstanceDropParent(documentStore.getDocument(), target.parentId)) {
      uiStore.setMessage("Cannot move layers inside a component instance");
      return;
    }

    const movableNodeIds = editableNodeIds(documentStore.getDocument(), nodeIds);
    if (movableNodeIds.length === 0) {
      return;
    }

    try {
      const context = commandContext();
      const nextSelection = selectionSnapshot({
        selectedNodeIds: movableNodeIds,
        primarySelectedNodeId: movableNodeIds.at(-1),
        mode: "select",
        selectedVertexId: undefined,
        selectedEdgeIds: [],
        selectedHandle: undefined,
      });
      historyRef.current.execute(new MoveNodesInTreeCommand(movableNodeIds, target), context, { afterSelection: nextSelection });
      applySelectionSnapshot(nextSelection);
      commit(context.document, movableNodeIds.length > 1 ? "Moved layers" : "Moved layer");
    } catch (error) {
      uiStore.setMessage(error instanceof Error ? error.message : "Could not move layer");
    }
  }

  function reorderFlexChild(nodeId: string, parentFrameId: string, index: number): void {
    runReorderFlexChild(commandHost, nodeId, parentFrameId, index);
  }

  function updateGradientPaint(nodeId: string, target: PaintTarget, paint: Paint): void {
    runUpdateGradientPaint(commandHost, nodeId, target, paint);
  }

  function openContextMenu(nodeId: string, point: { x: number; y: number }): void {
    uiStore.setContextMenu({ nodeId, x: point.x, y: point.y });
  }

  function centerViewportOnDocumentPoint(x: number, y: number): void {
    const target = viewStore.viewportTarget.getValue();
    viewStore.setViewportTarget({
      x,
      y,
      token: (target?.token ?? 0) + 1,
    });
  }

  function locateNodeInViewport(nodeId: string): void {
    const node = findSceneNode(documentStore.getDocument(), nodeId);
    if (!node) {
      uiStore.setMessage("Node not found");
      uiStore.setContextMenu(undefined);
      return;
    }

    centerViewportOnDocumentPoint(
      node.bounds.x + node.bounds.width / 2,
      node.bounds.y + node.bounds.height / 2,
    );
    uiStore.setContextMenu(undefined);
    uiStore.setMessage(`Located ${node.name}`);
  }

  function selectAndLocateNode(nodeId: string): void {
    const node = findSceneNode(documentStore.getDocument(), nodeId);
    if (!node) {
      uiStore.setMessage("Node not found");
      return;
    }

    selectNodes([nodeId], nodeId);
    locateNodeInViewport(nodeId);
  }

  function createPrototypeLink(sourceNodeId: string, targetFrameId: string): void {
    runCreatePrototypeLink(commandHost, sourceNodeId, targetFrameId);
  }

  function playPrototype(frameId: string): void {
    const frame = findSceneNode(documentStore.getDocument(), frameId);
    if (frame?.type !== "frame") {
      uiStore.setMessage("Select a frame to play");
      return;
    }

    uiStore.setPrototypePlayerFrameId(frameId);
  }

  function beginComponentPlacement(componentId: string): void {
    componentPlacementTokenRef.current += 1;
    viewStore.setComponentPlacement({ componentId, token: componentPlacementTokenRef.current });
  }

  function endComponentPlacement(): void {
    viewStore.setComponentPlacement(undefined);
  }

  function insertComponentInstance(componentId: string, point: { x: number; y: number }): void {
    try {
      runInsertComponentInstance(commandHost, componentId, point, { getActivePageId: () => resolvedActivePageId });
    } catch (error) {
      uiStore.setMessage(error instanceof Error ? error.message : "Could not insert instance");
    }
  }

  function locateComponent(componentId: string): void {
    const component = findSceneNode(documentStore.getDocument(), componentId);
    if (!component || component.type !== "component") {
      uiStore.setMessage("Component not found");
      return;
    }

    const pageId = findPageIdForSceneNode(documentStore.getDocument(), componentId);
    if (pageId && pageId !== resolvedActivePageId) {
      switchPage(pageId);
    }

    selectNodes([componentId], componentId);
    locateNodeInViewport(componentId);
  }

  function locateInstanceParentVariant(instanceId: string): void {
    const node = findSceneNode(documentStore.getDocument(), instanceId);
    if (node?.type !== "instance") {
      uiStore.setMessage("Select an instance");
      uiStore.setContextMenu(undefined);
      return;
    }

    const variant = findSceneNode(documentStore.getDocument(), node.variantId);
    if (variant?.type !== "componentVariant") {
      uiStore.setMessage("Parent variant not found");
      uiStore.setContextMenu(undefined);
      return;
    }

    selectNodes([variant.id], variant.id);
    locateNodeInViewport(variant.id);
  }

  async function copyNodeAs(nodeId: string, format: "svg" | "png", scale = 1): Promise<void> {
    const node = findSceneNode(documentStore.getDocument(), nodeId);
    if (!node) {
      uiStore.setMessage("Nothing to copy");
      uiStore.setContextMenu(undefined);
      return;
    }

    try {
      if (format === "svg") {
        const svg = await exportSceneNodeToSvg(node);
        await navigator.clipboard.writeText(svg);
        uiStore.setMessage(`Copied ${node.name} as SVG`);
      } else {
        await copySceneNodeAsPng(documentStore.getDocument(), node, scale);
        uiStore.setMessage(`Copied ${node.name} as ${pngScaleLabel(scale)}`);
      }
    } catch (error) {
      uiStore.setMessage(error instanceof Error ? error.message : "Copy failed");
    } finally {
      uiStore.setContextMenu(undefined);
    }
  }

  async function exportNodeAs(nodeId: string, format: "svg" | "png", scale = 1): Promise<void> {
    const node = findSceneNode(documentStore.getDocument(), nodeId);
    if (!node) {
      uiStore.setMessage("Nothing to export");
      uiStore.setContextMenu(undefined);
      return;
    }

    try {
      if (format === "svg") {
        const svg = await exportSceneNodeToSvg(node);
        const result = await downloadBytes(
          new TextEncoder().encode(svg),
          `${normalizedExportBaseName(node.name)}.svg`,
          "image/svg+xml;charset=utf-8",
        );
        uiStore.setMessage(result.method === "cancelled" ? "SVG export cancelled" : `Exported ${node.name} as SVG`);
      } else {
        const blob = await exportSceneNodeToPngBlob(documentStore.getDocument(), node, scale);
        const result = await downloadBytes(
          new Uint8Array(await blob.arrayBuffer()),
          `${normalizedExportBaseName(node.name)}${pngScaleSuffix(scale)}.png`,
          "image/png",
        );
        uiStore.setMessage(
          result.method === "cancelled"
            ? "PNG export cancelled"
            : `Exported ${node.name} as ${pngScaleLabel(scale)}`,
        );
      }
    } catch (error) {
      uiStore.setMessage(error instanceof Error ? error.message : "Export failed");
    } finally {
      uiStore.setContextMenu(undefined);
    }
  }

  function resetDuplicateContinuation(): void {
    runResetDuplicateContinuation();
  }

  function copySelectedVectorVertices(): boolean {
    return runCopySelectedVectorVertices(commandHost);
  }

  function canPasteVectorVertices(): boolean {
    return runCanPasteVectorVertices();
  }

  function pasteVectorVerticesFromClipboard(): boolean {
    return runPasteVectorVerticesFromClipboard(commandHost);
  }

  function pasteVectorSelectionAsObjectFromClipboard(): boolean {
    return runPasteVectorSelectionAsObjectFromClipboard(commandHost);
  }

  function duplicateVectorVerticesForDrag(
    nodeId: string,
    vertexIds: string[],
    activeVertexId: string,
  ): ReturnType<typeof runDuplicateVectorVerticesForDrag> {
    return runDuplicateVectorVerticesForDrag(commandHost, nodeId, vertexIds, activeVertexId);
  }

  async function cutSelectedObjects(): Promise<void> {
    return runCutSelectedObjects(commandHost);
  }

  async function copySelectedObjects(): Promise<void> {
    return runCopySelectedObjects(commandHost);
  }

  async function copyPrimarySelectionStyle(parts: StyleClipboardPart[]): Promise<void> {
    return runCopyPrimarySelectionStyle(commandHost, parts);
  }

  async function copyNodeStyle(nodeId: string, parts: StyleClipboardPart[]): Promise<void> {
    return runCopyNodeStyle(commandHost, nodeId, parts);
  }

  async function pasteStyleToSelection(nodeIds = selectedNodeIds): Promise<void> {
    return runPasteStyleToSelection(commandHost, nodeIds);
  }

  async function pasteObjects(): Promise<void> {
    return runPasteObjects(commandHost);
  }

  function pasteObjectPayload(payload: CurvaClipboardPayload): void {
    runPasteObjectPayload(commandHost, payload);
  }

  function pasteText(characters: string): void {
    runPasteText(commandHost, characters);
  }

  function pasteSvg(svgMarkup: string, insertPoint?: { x: number; y: number }): void {
    runPasteSvg(commandHost, svgMarkup, insertPoint);
  }

  function pasteImage(imagePaint: Extract<Paint, { type: "image" }>, insertPoint?: { x: number; y: number }): void {
    runPasteImage(commandHost, imagePaint, insertPoint);
  }

  function dropImageOnCanvas(dataTransfer: DataTransfer, point: { x: number; y: number }): void {
    runDropImageOnCanvas(commandHost, dataTransfer, point);
  }

  function duplicateSelectedObjects(offset?: { x: number; y: number }, nodeIds = selectedNodeIds, label = "Duplicate Selection", dropPoint?: { x: number; y: number }): void {
    runDuplicateSelectedObjects(commandHost, offset, nodeIds, label, dropPoint);
  }

  function copySelectedAnimationNodes(): void {
    void runCopySelectedAnimationNodes();
  }

  function pasteAnimationNodes(): void {
    void runPasteAnimationNodes(commandHost);
  }

  function duplicateSelectedAnimationNodes(targetPositions?: Array<{ nodeId: string; position: { x: number; y: number } }>): void {
    runDuplicateSelectedAnimationNodes(commandHost, targetPositions);
  }

  function createComponentFromSelection(nodeIds = selectedNodeIds): void {
    if (mode === "textEdit") {
      return;
    }
    runCreateComponent(commandHost, nodeIds, {
      getActivePageId: () => resolvedActivePageId,
      onDuplicateContinuationReset: resetDuplicateContinuation,
      onComponentCreated: (document, componentId) => void refreshComponentThumbnail(document, componentId),
    });
    uiStore.setContextMenu(undefined);
  }

  function addComponentVariant(componentId: string): void {
    runAddComponentVariant(commandHost, componentId, { getActivePageId: () => resolvedActivePageId, onDuplicateContinuationReset: resetDuplicateContinuation });
  }

  function deleteComponentVariant(componentId: string, variantId: string): void {
    runDeleteComponentVariant(commandHost, componentId, variantId, { getActivePageId: () => resolvedActivePageId, onDuplicateContinuationReset: resetDuplicateContinuation });
  }

  function updateInstanceVariant(instanceId: string, variantId: string): void {
    runUpdateInstanceVariant(commandHost, instanceId, variantId, { getActivePageId: () => resolvedActivePageId, onDuplicateContinuationReset: resetDuplicateContinuation });
  }

  function detachInstance(instanceId: string): void {
    runDetachInstance(commandHost, instanceId, { getActivePageId: () => resolvedActivePageId, onDuplicateContinuationReset: resetDuplicateContinuation });
    uiStore.setContextMenu(undefined);
  }

  function nudgeNodes(nodeIds: string[], dx: number, dy: number): void {
    runNudgeNodes(commandHost, nodeIds, dx, dy);
  }

  function nudgeSelection(dx: number, dy: number): void {
    runNudgeSelection(commandHost, dx, dy);
  }

  function nudgeSelectedVectorGeometry(dx: number, dy: number): boolean {
    return runNudgeSelectedVectorGeometry(commandHost, dx, dy);
  }

  function nudgeVectorVertexPositions(
    nodeId: string,
    positions: Array<{ vertexId: string; x: number; y: number }>,
    selection: { selectedVertexId?: string; selectedVertexIds: string[]; selectedEdgeIds: string[]; label: string },
  ): boolean {
    return runNudgeVectorVertexPositions(commandHost, nodeId, positions, selection);
  }

  function deleteNodes(nodeIds: string[]): void {
    runDeleteNodes(commandHost, nodeIds);
  }

  function selectPrototypeLink(linkId: string | undefined): void {
    runSelectPrototypeLink(commandHost, linkId);
  }

  function pauseAnimation(): void {
    uiStore.patchAnimation({ playing: false });
  }

  function resetAnimationPlayback(): void {
    uiStore.patchAnimation({ playing: false });
    uiStore.patchAnimation({ timeMs: 0 });
  }

  function activateAnimationRoot(rootId: string | undefined): void {
    runActivateAnimationRoot(rootId, activeAnimationRootId);
  }

  function activateAnimationRootForNode(nodeId: string | undefined, graph = animationGraph): void {
    runActivateAnimationRootForNode(nodeId, graph);
  }

  async function exportActiveAnimation(format: AnimationExportFormat): Promise<void> {
    if (animationExportingFormat) {
      return;
    }

    uiStore.patchAnimation({ playing: false });
    uiStore.patchAnimation({ exportingFormat: format });
    let lastReportedPercent = -1;
    try {
      const result = await exportAnimation({
        document: documentStore.getDocument(),
        rootNodeId: activeAnimationRootId,
        format,
        fps: 30,
        scale: defaultAnimationExportScale(),
        onProgress: progress => {
          const percent = Math.floor(progress * 100);
          if (percent !== lastReportedPercent && (percent === 100 || percent % 5 === 0)) {
            lastReportedPercent = percent;
            uiStore.setMessage(`Exporting ${format.toUpperCase()} ${percent}%`);
          }
        },
      });
      const exportResult = await downloadBytes(
        new Uint8Array(await result.blob.arrayBuffer()),
        `${normalizedExportBaseName(result.frameName)}.${result.filenameExtension}`,
        result.mimeType,
      );
      if (exportResult.method === "cancelled") {
        uiStore.setMessage(`${format.toUpperCase()} export cancelled`);
      } else {
        uiStore.setMessage(`Exported ${result.frameName} as ${format.toUpperCase()}${result.codec ? ` (${result.codec})` : ""}`);
      }
    } catch (error) {
      uiStore.setMessage(error instanceof Error ? error.message : "Animation export failed");
    } finally {
      uiStore.patchAnimation({ exportingFormat: undefined });
    }
  }

  async function copyActiveAnimationAsMermaid(): Promise<void> {
    if (!navigator.clipboard?.writeText) {
      uiStore.setMessage("Clipboard text copy is not supported");
      return;
    }

    try {
      const result = exportDocumentAnimationToMermaid({
        document: documentStore.getDocument(),
        rootNodeId: activeAnimationRootId,
      });
      await navigator.clipboard.writeText(result.mermaid);
      uiStore.setMessage(`Copied ${result.frameName} as Mermaid`);
    } catch (error) {
      uiStore.setMessage(error instanceof Error ? error.message : "Copy failed");
    }
  }

  function bakeActiveAnimationToFrame(): void {
    runBakeActiveAnimationToFrame(commandHost, activeAnimationRootId, animationTimeMs);
  }

  function insertAnimationNode(node: AnimationGraphNode): void {
    runInsertAnimationNode(commandHost, node);
  }

  function updateAnimationNode(nextNode: AnimationGraphNode, label = "Updated animation node"): void {
    runUpdateAnimationNode(commandHost, nextNode, label);
  }

  function moveAnimationNodes(
    before: Array<{ nodeId: string; position: { x: number; y: number } }>,
    after: Array<{ nodeId: string; position: { x: number; y: number } }>,
  ): void {
    runMoveAnimationNodes(commandHost, before, after);
  }

  function updateAnimationVisualGroup(nextGroup: AnimationGraphVisualGroup, label = "Updated animation group"): void {
    runUpdateAnimationVisualGroup(commandHost, nextGroup, label);
  }

  function deleteAnimationVisualGroup(groupId: string): void {
    runDeleteAnimationVisualGroup(commandHost, groupId);
  }

  function groupSelectedAnimationNodes(): void {
    runGroupSelectedAnimationNodes(commandHost);
  }

  function connectAnimation(connection: AnimationConnection): void {
    runConnectAnimation(commandHost, connection);
  }

  function insertAnimationNodeAndConnect(node: AnimationGraphNode, connection: AnimationConnection): void {
    runInsertAnimationNodeAndConnect(commandHost, node, connection);
  }

  function createSceneObjectAnimateNode(sourceNodeId: string, position: { x: number; y: number }): void {
    runCreateSceneObjectAnimateNode(commandHost, sourceNodeId, position);
  }

  function createSceneObjectsGroupNode(sourceNodeIds: string[], position: { x: number; y: number }): void {
    runCreateSceneObjectsGroupNode(commandHost, sourceNodeIds, position);
  }

  function connectSceneObjectToAnimation(sourceNodeId: string, endpoint: AnimationEndpoint): void {
    runConnectSceneObjectToAnimation(commandHost, sourceNodeId, endpoint);
  }

  function selectAnimationNodes(nodeIds: string[], primaryNodeId?: string): void {
    runSelectAnimationNodes(commandHost, nodeIds, primaryNodeId);
  }

  function selectAnimationConnections(connectionIds: string[]): void {
    runSelectAnimationConnections(commandHost, connectionIds);
  }

  function selectObjectConnections(keys: string[]): void {
    runSelectObjectConnections(keys);
  }

  function useSceneSelectionForAnimationNode(): void {
    if (!selectedAnimationNode || selectedNodeIds.length === 0) {
      return;
    }
    if (selectedAnimationNode.kind === "group") {
      updateAnimationNode({
        ...selectedAnimationNode,
        targets: selectedNodeIds.map(nodeId => ({ kind: "sceneNode" as const, nodeId })),
      }, "Updated animation group");
      return;
    }
    if (selectedAnimationNode.kind === "animate") {
      updateAnimationNode({
        ...selectedAnimationNode,
        target: { kind: "sceneNode", nodeId: selectedNodeIds[0]! },
      }, "Updated animation target");
    }
  }

  function addAnimateProperty(nodeId = primarySelectedAnimationNodeId): void {
    runAddAnimateProperty(commandHost, nodeId);
  }

  function deleteSelectedAnimationItems(): boolean {
    return runDeleteSelectedAnimationSelection(commandHost);
  }

  function deleteSelectedPrototypeLink(): boolean {
    if (!selectedPrototypeLinkId) {
      return false;
    }

    const context = commandContext();
    historyRef.current.execute(
      new DeletePrototypeLinkCommand(selectedPrototypeLinkId),
      context,
      { afterSelection: currentSelection() },
    );
    uiStore.setSelectedPrototypeLinkId(undefined);
    commit(context.document, "Deleted prototype link");
    return true;
  }

  function addGuide(scope: GuideScope, guide: Guide): void {
    runAddGuide(commandHost, scope, guide, {
      onSelectedGuideChange: selection => {
        selectedGuideRef.current = selection;
      },
    });
  }

  function moveGuide(selection: GuideSelection, fromPosition: number, toPosition: number): void {
    runMoveGuide(commandHost, selection, fromPosition, toPosition, {
      onSelectedGuideChange: nextSelection => {
        selectedGuideRef.current = nextSelection;
      },
    });
  }

  function relocateGuide(
    selection: GuideSelection,
    fromPosition: number,
    toScope: GuideScope,
    toPosition: number,
  ): void {
    runRelocateGuide(commandHost, selection, fromPosition, toScope, toPosition, {
      onSelectedGuideChange: nextSelection => {
        selectedGuideRef.current = nextSelection;
      },
    });
  }

  function updateSelectedGuide(selection: GuideSelection | undefined): void {
    runUpdateSelectedGuide(selection, {
      onSelectedGuideChange: nextSelection => {
        selectedGuideRef.current = nextSelection;
      },
    });
  }

  function deleteSelectedGuide(): boolean {
    const guide = selectedGuideRef.current;
    if (!guide) {
      return false;
    }

    deleteGuide(guide);
    return true;
  }

  function deleteGuide(selection: GuideSelection): void {
    runDeleteGuide(commandHost, selection, {
      onSelectedGuideChange: nextSelection => {
        selectedGuideRef.current = nextSelection;
      },
    });
  }

  function setShowRulersPreference(show: boolean): void {
    uiStore.setShowRulers(show);
  }

  function setSnapToGuidesPreference(snap: boolean): void {
    uiStore.setSnapToGuides(snap);
  }

  function setAnimationTimelineOpenPreference(open: boolean): void {
    uiStore.setAnimationTimelineOpen(open);
  }

  function setAnimationPreviewWindowOpenPreference(open: boolean): void {
    uiStore.setAnimationPreviewWindowOpen(open);
  }

  function reorderNodes(nodeIds: string[], placement: ReorderPlacement): void {
    runReorderNodes(commandHost, nodeIds, placement);
  }

  function swapFillAndStrokeForSelection(): void {
    const nodes = getAllSceneNodes(documentStore.getDocument()).filter(node => selectedNodeIds.includes(node.id) && !isNodeLockedForEditing(documentStore.getDocument(), node.id));
    const commands: Command[] = [];

    for (const node of nodes) {
      appendSwapFillStrokeCommands(commands, documentStore.getDocument(), node);
    }

    if (commands.length === 0) {
      uiStore.setMessage("Nothing to swap");
      return;
    }

    const context = commandContext();
    historyRef.current.execute(
      commands.length === 1 ? commands[0]! : new TransactionCommand("Swap Fill and Stroke", commands),
      context,
      { afterSelection: currentSelection() },
    );
    commit(context.document, commands.length > 1 ? "Swapped fills and strokes" : "Swapped fill and stroke");
  }

  function replaceSelectionPaint(scope: SelectionColorScope, fromPaint: Paint, toPaint: Paint): void {
    if (paintsEqual(fromPaint, toPaint)) {
      return;
    }

    const context = commandContext();
    const scopeNodes = collectSelectionScopeNodes(context.document, selectedNodeIds)
      .filter(node => !isNodeLockedForEditing(context.document, node.id));
    const commands: Command[] = [];

    for (const node of scopeNodes) {
      if (nodeContributesOwnPaints(node)) {
        const nextStyle = replacePaintInNodeStyle(node.style, fromPaint, toPaint, scope);
        if (!styleEqual(node.style, nextStyle)) {
          commands.push(new UpdateNodeStyleCommand(node.id, node.style, nextStyle));
        }
      }

      if ((scope === "fill" || scope === "all") && node.type === "text") {
        const nextText = replacePaintInTextModel(node.text, fromPaint, toPaint);
        if (nextText) {
          commands.push(new UpdateTextCommand(node.id, node.text, nextText));
        }
      }
    }

    if (commands.length === 0) {
      return;
    }

    const label = scope === "fill" ? "Updated fill colors" : scope === "stroke" ? "Updated stroke colors" : "Updated colors";
    historyRef.current.execute(
      commands.length === 1 ? commands[0]! : new TransactionCommand(label, commands),
      context,
      { afterSelection: currentSelection() },
    );
    commit(context.document, label);
  }

  function createDocumentStyle(
    nodeId: string,
    kind: DocumentStyleKind,
    name: string,
    options: CreateStyleFromSelectionOptions = {},
  ): string | undefined {
    return runCreateDocumentStyle(commandHost, nodeId, kind, name, options);
  }

  function applyDocumentStyle(nodeId: string, styleId: string): void {
    runApplyDocumentStyle(commandHost, nodeId, styleId);
  }

  function detachDocumentStyle(nodeId: string, kind: DocumentStyleKind): void {
    runDetachDocumentStyle(commandHost, nodeId, kind);
  }

  function updateDocumentStyle(style: DocumentStyle): void {
    runUpdateDocumentStyle(commandHost, style);
  }

  function deleteDocumentStyle(styleId: string): void {
    runDeleteDocumentStyle(commandHost, styleId);
  }

  function groupSelectedNodes(): void {
    runGroupSelectedNodes(commandHost);
  }

  function frameSelectedNodes(): void {
    runFrameSelectedNodes(commandHost);
  }

  function ungroupSelectedNodes(): void {
    runUngroupSelectedNodes(commandHost);
  }

  function convertFrameGroup(nodeId: string): void {
    runConvertFrameGroup(commandHost, nodeId);
    uiStore.setContextMenu(undefined);
  }

  async function applyBooleanOperation(operation: BooleanOperation): Promise<void> {
    return runApplyBooleanOperation(commandHost, operation);
  }

  async function flattenSelectedNodes(options: { planarize?: boolean; nodeIds?: string[] } = {}): Promise<void> {
    return runFlattenSelectedNodes(commandHost, options);
  }

  async function outlineStrokeForSelection(nodeIds = selectedNodeIds): Promise<void> {
    return runOutlineStrokeForSelection(commandHost, nodeIds);
  }

  function enterVectorMode(nodeId = primarySelectedNodeId): void {
    runEnterVectorMode(commandHost, nodeId);
  }

  function exitVectorMode(): void {
    cancelSubdivideSession(false);
    runExitVectorMode(commandHost);
  }

  function splitToRegions(): void {
    if (!primarySelectedNodeId) {
      return;
    }
    runSplitVectorNetworkToRegionObjects(commandHost, primarySelectedNodeId);
  }

  function cancelPathDrawing(): boolean {
    return runCancelPathDrawing(commandHost);
  }

  function enterTextMode(nodeId = primarySelectedNodeId): void {
    runEnterTextMode(commandHost, textEditSessionRef, nodeId);
  }

  function exitTextMode(): void {
    runExitTextMode(commandHost, textEditSessionRef);
  }

  function commitTextEditSession(): void {
    runCommitTextEditSession(commandHost, textEditSessionRef);
  }
  leaveTextEditRef.current = commitTextEditSession;

  function enterEditMode(): void {
    runEnterEditMode(commandHost, textEditSessionRef, primarySelectedNodeId);
  }

  function enterVectorTargeting(nodeId = primarySelectedNodeId): void {
    runEnterVectorTargeting(commandHost, nodeId);
  }

  function exitVectorTargeting(): void {
    runExitVectorTargeting(commandHost);
  }

  function clearVectorTargetingSelection(): void {
    runClearVectorTargetingSelection(commandHost);
  }

  const updateVectorVertexSettings = bindCommand(commandHost, runUpdateVectorVertexSettings);

  function drawPencilStroke(points: Array<{ x: number; y: number }>): void {
    const parentFrameId = topFrameIdAtPoint(documentStore.getDocument(), points[0] ?? { x: 0, y: 0 }, resolvedActivePageId) ?? activePageFrameId;
    if (!parentFrameId) {
      uiStore.setMessage("No frame available");
      return;
    }

    const network = vectorizePencilStroke(points);
    if (!network) {
      uiStore.setMessage("Stroke too short");
      return;
    }

    const parentFrame = findSceneNode(documentStore.getDocument(), parentFrameId);
    const count = parentFrame?.type === "frame" ? parentFrame.children.length : findFirstFrameNode(documentStore.getDocument())?.children.length ?? 0;
    const draftNode = createPencilVectorNetworkNode(`Pencil ${count + 1}`, network);
    const node = placeNodeInParent(parentFrameId, draftNode, draftNode.bounds);
    const nextSelection = selectionSnapshot({
      selectedNodeIds: [node.id],
      primarySelectedNodeId: node.id,
      mode: "select",
      selectedVertexId: undefined,
      selectedEdgeIds: [],
      selectedHandle: undefined,
    });
    const context = commandContext();
    historyRef.current.execute(new InsertNodeCommand(parentFrameId, node), context, { afterSelection: nextSelection });
    applySelectionSnapshot(nextSelection);
    selectionStore.setActiveTool("pencil");
    commit(context.document, `Drew ${node.name}`);
  }

  function commitShapeBuilderSelection(nodeId: string, selection: ShapeBuilderSelection): void {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
    if (node?.type !== "vectorNetwork") {
      uiStore.setMessage("Shape builder needs an editable vector network");
      return;
    }

    const nextNetwork = materializeShapeBuilderNetwork(node.network, selection);
    if (!nextNetwork || nextNetwork.vertices.length === 0 || nextNetwork.edges.length === 0) {
      uiStore.setMessage("Shape builder result is empty");
      return;
    }

    const insertion = insertionPointAboveNode(documentStore.getDocument(), node.id);
    if (!insertion) {
      uiStore.setMessage("Shape builder could not place result");
      return;
    }

    const resultId = createId("node");
    const nextNode: VectorNetworkNode = {
      ...structuredClone(node),
      id: resultId,
      name: `${node.name} Shape Builder`,
      bounds: getVectorNetworkBounds(nextNetwork),
      network: nextNetwork,
    };
    const context = commandContext();
    const nextSelection = selectionSnapshot({
      selectedNodeIds: [resultId],
      primarySelectedNodeId: resultId,
      mode: "vectorEdit",
      selectedVertexId: undefined,
      selectedVertexIds: [],
      selectedEdgeIds: [],
      selectedHandle: undefined,
    });
    historyRef.current.execute(new InsertNodeCommand(insertion.parentId, nextNode, insertion.index), context, { afterSelection: nextSelection });
    applySelectionSnapshot(nextSelection);
    selectionStore.setActiveTool("select");
    commit(context.document, "Shape builder result");
  }

  function selectVectorVertex(vertexId: string | undefined): void {
    updateSelection(selectionSnapshot({ selectedVertexId: vertexId, selectedEdgeIds: [], selectedHandle: undefined }), true);
  }

  function selectVectorVertices(vertexIds: string[]): void {
    updateSelection(selectionSnapshot({ selectedVertexId: vertexIds.at(-1), selectedVertexIds: vertexIds, selectedEdgeIds: [], selectedHandle: undefined }), true);
  }

  function selectVectorHandle(handle: { edgeId: string; endpoint: "from" | "to" } | undefined): void {
    updateSelection(selectionSnapshot({ selectedHandle: handle, selectedVertexId: undefined, selectedEdgeIds: [] }), true);
  }

  function selectVectorEdges(edgeIds: string[]): void {
    updateSelection(selectionSnapshot({ selectedEdgeIds: edgeIds, selectedVertexId: undefined, selectedHandle: undefined }), true);
  }

  function toggleUiChrome(): void {
    const currentMode = uiStore.uiChromeMode.getValue();
    const nextMode: UiChromeMode = currentMode === "hidden" ? "visible" : "hidden";
    uiStore.setUiChromeMode(nextMode);
    uiStore.setMessage(nextMode === "hidden" ? "UI hidden" : "UI shown");
  }

  function toggleFloatingToolChrome(): void {
    const currentMode = uiStore.uiChromeMode.getValue();
    const nextMode: UiChromeMode = currentMode === "tools" ? "visible" : "tools";
    uiStore.setUiChromeMode(nextMode);
    uiStore.setMessage(nextMode === "tools" ? "Tools only" : "UI shown");
  }

  function toggleFloatingToolSidebarChrome(): void {
    const currentMode = uiStore.uiChromeMode.getValue();
    const nextMode: UiChromeMode = currentMode === "toolsSidebar" ? "visible" : "toolsSidebar";
    uiStore.setUiChromeMode(nextMode);
    uiStore.setMessage(nextMode === "toolsSidebar" ? "Tools + sidebar" : "UI shown");
  }

  function toggleRulers(): void {
    const next = !uiStore.canvasDebug.getValue().showRulers;
    uiStore.setShowRulers(next);
    uiStore.setMessage(next ? "Rulers shown" : "Rulers hidden");
  }

  function toggleRenderDebug(): void {
    const next = !uiStore.canvasDebug.getValue().showRenderDebug;
    uiStore.patchCanvasDebug({ showRenderDebug: next });
    uiStore.setMessage(next ? "Render debug on" : "Render debug off");
  }

  function resetZoom(): void {
    viewStore.bumpResetZoomToken();
    uiStore.setMessage("Zoom 100%");
  }

  function applySampledColorToSelection(color: OklchColor, target: "fill" | "stroke"): void {
    const paint = createSolidPaint(color);
    const nodeIds = editableSelection();
    const commands: Command[] = [];
    const detachKind = target === "fill" ? "fill" as const : "stroke" as const;

    for (const nodeId of nodeIds) {
      const node = findSceneNode(documentStore.getDocument(), nodeId);
      if (!node || isNodeLockedForEditing(documentStore.getDocument(), nodeId)) {
        continue;
      }

      let workingNode = node;
      if (styleBindingId(workingNode, detachKind)) {
        commands.push(new DetachDocumentStyleCommand(nodeId, detachKind));
        workingNode = inlineStyleBinding(documentStore.getDocument(), workingNode, detachKind);
      }

      if (target === "fill") {
        if (workingNode.type === "text") {
          const nextText = patchTextTypeStyleForInspector(workingNode.text, { fill: paint });
          if (!textEqual(workingNode.text, nextText)) {
            commands.push(new UpdateTextCommand(nodeId, workingNode.text, nextText));
          }
          continue;
        }

        if (!nodeContributesOwnPaints(workingNode)) {
          continue;
        }

        const nextStyle: NodeStyle = { ...workingNode.style, fills: [structuredClone(paint)] };
        if (!styleEqual(workingNode.style, nextStyle)) {
          commands.push(new UpdateNodeStyleCommand(nodeId, workingNode.style, nextStyle));
        }
        continue;
      }

      if (!nodeContributesOwnPaints(workingNode) && workingNode.type !== "text") {
        continue;
      }

      const strokes = normalizeStrokeStack(workingNode.style);
      const baseStroke = strokes[0] ?? { paint: createSolidPaint("#000000"), width: 1, align: "center" as const };
      const nextStyle = styleWithStrokeStack(workingNode.style, [{ ...baseStroke, paint: structuredClone(paint) }]);
      if (!styleEqual(workingNode.style, nextStyle)) {
        commands.push(new UpdateNodeStyleCommand(nodeId, workingNode.style, nextStyle));
      }
    }

    if (commands.length === 0) {
      return;
    }

    const context = commandContext();
    const label = target === "fill" ? "Applied sampled fill" : "Applied sampled stroke";
    historyRef.current.execute(
      commands.length === 1 ? commands[0]! : new TransactionCommand(label, commands),
      context,
      { afterSelection: currentSelection() },
    );
    commit(context.document, label);
  }

  function startSelectionColorSample(): void {
    if (mode === "textEdit" || selectedNodeIds.length === 0) {
      return;
    }

    requestColorSample({
      sourceId: SELECTION_COLOR_SAMPLE_SOURCE_ID,
      mode: "hex",
      onSample: (color, sampleStroke) => {
        applySampledColorToSelection(color, sampleStroke ? "stroke" : "fill");
        changeTool("select");
      },
    });
  }

  function selectAllNodes(): void {
    const nodeIds = selectAllNodeIds(documentStore.getDocument(), resolvedActivePageId, selectedNodeIds, primarySelectedNodeId);
    if (nodeIds.length === 0) {
      uiStore.setMessage("Nothing to select");
      return;
    }

    selectNodes(nodeIds, nodeIds.at(-1));
    uiStore.setMessage(nodeIds.length === 1 ? "Selected 1 object" : `Selected ${nodeIds.length} objects`);
  }

  const topBarActions = createTopBarActions(commandHost, {
    changeTool,
    changeShapeKind,
    applyBooleanOperation: operation => void applyBooleanOperation(operation),
    flattenSelectedNodes: options => void flattenSelectedNodes(options),
    requestNewDocument,
    requestCurvaImport: () => void requestCurvaImport(),
    requestComponentImport: () => void requestComponentImport(),
    requestScriptImport: () => void requestScriptImport(),
    requestScriptImportAndReplace: () => void requestScriptImportAndReplace(),
    requestAddEditorScripts: () => void requestAddEditorScripts(),
    requestReplaceEditorScripts: () => void requestReplaceEditorScripts(),
    clearEditorScripts: () => clearEditorScriptsLibrary(),
    openEditorScriptsDocument: () => void openEditorScriptsDocument(),
    downloadEditorScriptsDocument: () => void downloadEditorScriptsDocumentCopy(),
    exportCurvaFile: () => void exportCurvaFile(),
    exportCurvaFileAs: () => void exportCurvaFileAs(),
    exportSelectedFramesPdf: () => void exportSelectedFramesPdf(),
    renameDocument,
    toggleAnimationMode: () => setAnimationModeOpen(!animationPanelOpen, true),
    startSelectionColorSample,
    enterSubdivide,
    setSubdivideSegments,
    commitSubdivide,
    splitToRegions,
  });

  const documentActions = createDocumentActions(commandHost, {
    getActivePageId: () => resolvedActivePageId,
    onBoundsPreviewChange: updateBoundsPreview,
    onRotationOriginCommit: updateRotationOrigin,
    onDuplicateDrag: (nodeIds, offset, dropPoint) => duplicateSelectedObjects(offset, nodeIds, "Alt Drag Duplicate", dropPoint),
    onContextMenu: openContextMenu,
    commitColorSample,
    onComponentPlacementEnd: endComponentPlacement,
    onComponentPlacementCommit: insertComponentInstance,
    onGuideSelectionChange: updateSelectedGuide,
    onGuideAdd: addGuide,
    onGuideMove: moveGuide,
    onGuideRelocate: relocateGuide,
    onGuideDelete: deleteGuide,
    onPrototypeLinkCreate: createPrototypeLink,
    onRenameNode: renameNode,
    onTextInsert: insertTextAt,
    onTextEditExit: exitTextMode,
    onTextSelectionChange: updateTextSelectionState,
    onEnterTextMode: enterTextMode,
    onScriptEntryDrop: (scriptId, contextFrameId, source = "document") => {
      void runDocumentScript(scriptId, {
        contextFrameId,
        scriptLibrarySource: source,
      });
    },
    onEditFrameScript: openAttachedFrameScriptEditor,
    onRunFrameScript: frameId => {
      void runFrameAttachedScript(frameId);
    },
  });

  const vectorActions = createVectorActions(commandHost, {
    getActivePageId: () => resolvedActivePageId,
    textEditSessionRef,
    getPrimarySelectedNodeId: () => primarySelectedNodeId,
    onEnterVectorMode: () => enterVectorMode(),
    onExitVectorMode: exitVectorMode,
    onSelectedVertexChange: selectVectorVertex,
    onSelectedVerticesChange: selectVectorVertices,
    onSelectedEdgesChange: selectVectorEdges,
    onSelectedHandleChange: selectVectorHandle,
    onShapeBuilderCommit: commitShapeBuilderSelection,
    onPencilDraw: drawPencilStroke,
  });

  const animationActions = createAnimationActions(commandHost, {
    getAnimationGraph: () => animationGraph,
    getActiveAnimationRootId: () => activeAnimationRootId,
    onAnimationNodeArm: kind => uiStore.patchAnimation({ armedNodeKind: kind }),
    onAnimationPause: pauseAnimation,
  });

  const editorActions = useMemo(
    () => {
      const actions = createEditorActions({
        mode,
        devModeActive: rightPanelTab === "dev" || animationPanelOpen,
        animationPanelOpen,
        vectorTargetNodeId,
        hasVectorTargetingSubselection: Boolean(vectorTargetNodeId) && (selectedVertexIds.length > 0 || (selectedEdgeIds?.length ?? 0) > 0 || Boolean(selectedHandle)),
        hasSelection: selectedNodeIds.length > 0 || (animationPanelOpen && (selectedAnimationNodeIds.length > 0 || selectedAnimationConnectionIds.length > 0 || selectedObjectConnectionKeys.length > 0)),
        hasGuideSelection: selectedGuide !== undefined,
        hasVectorSelection: mode === "vectorEdit" && (selectedVertexIds.length > 0 || selectedEdgeIds.length > 0 || Boolean(selectedVertexId)),
        hasVectorVertexSelection: mode === "vectorEdit" && (selectedVertexIds.length > 0 || Boolean(selectedVertexId)),
        isColorSamplerActive: colorSampler !== undefined,
        selectedCount: animationPanelOpen ? selectedAnimationNodeIds.length : selectedNodeIds.length,
        setPreviews: () => undefined,
        setTool: changeTool,
        adjustSculptBrush: (property, delta) => {
          const current = uiStore.sculptBrush.getValue();
          uiStore.patchSculptBrush({ [property]: current[property] + delta });
        },
        setShapeTool: changeShapeKind,
        clearSelection: () => {
          selectNodes([], undefined);
          updateSelectedGuide(undefined);
          uiStore.setSelectedPrototypeLinkId(undefined);
          selectAnimationNodes([], undefined);
        },
        selectAll: selectAllNodes,
        startSelectionColorSample,
        openSelectionPaint,
        cancelColorSample,
        cancelPathDrawing,
        cancelSubdivide: () => cancelSubdivideSession(true),
        commitSubdivideSession: commitSubdivide,
        exitVectorMode,
        exitTextMode,
        enterEditMode,
        isVectorTargeting: () => Boolean(uiStore.animation.getValue().vectorTargetNodeId),
        exitVectorTargeting,
        clearVectorTargetingSelection,
        deleteSelection: options => {
          if (mode === "textEdit") {
            return;
          }
          if (animationPanelOpen && deleteSelectedAnimationItems()) {
            return;
          }
          if (deleteSelectedGuide()) {
            return;
          }
          if (deleteSelectedPrototypeLink()) {
            return;
          }
          if (mode === "vectorEdit") {
            if (!deleteSelectedVectorSelectionFromHost(commandHost, options)) {
              uiStore.setMessage("Select a vertex, edge, or control point to delete");
            }
            return;
          }

          if (!deleteSelectedVectorSelectionFromHost(commandHost, options)) {
            deleteNodes(selectedNodeIds);
          }
        },
        copySelection: () => {
          if (animationPanelOpen && selectedAnimationNodeIds.length > 0) {
            copySelectedAnimationNodes();
          } else {
            void copySelectedObjects();
          }
        },
        cutSelection: () => {
          if (animationPanelOpen && selectedAnimationNodeIds.length > 0 && animationGraph) {
            copySelectedAnimationNodes();
            const nodes = animationGraph.nodes.filter(n => selectedAnimationNodeIds.includes(n.id));
            if (nodes.length > 0) {
              const context = commandContext();
              const nextSelection = selectionSnapshot({ selectedAnimationNodeIds: [], primarySelectedAnimationNodeId: undefined });
              historyRef.current.execute(new DeleteAnimationNodesCommand(nodes), context, { afterSelection: nextSelection });
              applySelectionSnapshot(nextSelection);
              commit(context.document, nodes.length > 1 ? `Cut ${nodes.length} animation nodes` : "Cut animation node");
            }
          } else {
            void cutSelectedObjects();
          }
        },
        pasteSelection: () => {
          if (animationPanelOpen) {
            // Reads the system clipboard (cross-instance) with the in-memory
            // store as fallback; shows a message when nothing is available.
            pasteAnimationNodes();
            return;
          }
          void pasteObjects();
        },
        copyStyle: () => void copyPrimarySelectionStyle(["fill", "stroke", "effects", "opacity", "cornerRadii"]),
        pasteStyle: () => void pasteStyleToSelection(),
        duplicateSelection: () => {
          if (animationPanelOpen && selectedAnimationNodeIds.length > 0) {
            duplicateSelectedAnimationNodes();
          } else {
            duplicateSelectedObjects();
          }
        },
        swapFillAndStroke: swapFillAndStrokeForSelection,
        groupSelection: () => {
          if (animationPanelOpen && selectedAnimationNodeIds.length > 0) {
            groupSelectedAnimationNodes();
          } else {
            groupSelectedNodes();
          }
        },
        frameSelection: frameSelectedNodes,
        ungroupSelection: ungroupSelectedNodes,
        booleanSelection: operation => void applyBooleanOperation(operation),
        outlineStrokeSelection: () => void outlineStrokeForSelection(),
        flattenSelection: () => void flattenSelectedNodes(),
        flattenPlanarizedSelection: () => void flattenSelectedNodes({ planarize: true }),
        createComponentSelection: () => createComponentFromSelection(),
        reorderSelection: placement => reorderNodes(selectedNodeIds, placement),
        alignSelection: alignSelectedNodes,
        flipSelection: flipSelectedNodes,
        nudgeSelection,
        undo,
        redo,
        resetZoom,
        toggleUiChrome,
        toggleFloatingToolChrome,
        toggleFloatingToolSidebarChrome,
        toggleAnimationPreviewWindow: () => setAnimationPreviewWindowOpenPreference(!animationPreviewWindowOpen),
        toggleAllowScripts: () => uiStore.setAllowScripts(!uiStore.allowScripts.getValue()),
        toggleScriptsDebugWindow: () => uiStore.setScriptsDebugWindowOpen(!uiStore.scriptsDebugWindowOpen.getValue()),
        toggleRulers,
        toggleRenderDebug,
        openDocument: () => void requestCurvaImport(),
        newDocument: requestNewDocument,
        saveDocument: () => void exportCurvaFile(),
        saveDocumentAs: () => void exportCurvaFileAs(),
        setMessage: uiStore.setMessage,
      });
      return isNativeShell() ? stripNativeMenuOwnedShortcuts(actions) : actions;
    },
    [activeTool, animationGraph, animationPanelOpen, animationPreviewWindowOpen, document, mode, primarySelectedNodeId, resolvedActivePageId, colorSampler, selectedAnimationConnectionIds, selectedAnimationNodeIds, selectedEdgeIds, selectedGuide, selectedHandle, selectedNodeIds, selectedPrototypeLinkId, selectedVertexId, selectedVertexIds, activeAnimationRootId, selectedObjectConnectionKeys, vectorTargetNodeId],
  );

  const nativeMenuHandlersRef = useRef<NativeMenuHandlers>({
    runAction: () => undefined,
    requestComponentImport: () => undefined,
    requestScriptImport: () => undefined,
    requestScriptImportAndReplace: () => undefined,
    requestAddEditorScripts: () => undefined,
    requestReplaceEditorScripts: () => undefined,
    clearEditorScripts: () => undefined,
    openEditorScriptsDocument: () => undefined,
    downloadEditorScriptsDocument: () => undefined,
    exportSelectedFramesPdf: () => undefined,
    openRecentPath: () => undefined,
    clearRecents: () => undefined,
  });
  nativeMenuHandlersRef.current = {
    runAction: actionId => {
      const action = editorActions.find(entry => entry.id === actionId);
      if (!action || action.isEnabled?.() === false) {
        return;
      }
      action.run(new KeyboardEvent("keydown"));
    },
    requestComponentImport: () => {
      void requestComponentImport();
    },
    requestScriptImport: () => {
      void requestScriptImport();
    },
    requestScriptImportAndReplace: () => {
      void requestScriptImportAndReplace();
    },
    requestAddEditorScripts: () => {
      void requestAddEditorScripts();
    },
    requestReplaceEditorScripts: () => {
      void requestReplaceEditorScripts();
    },
    clearEditorScripts: () => {
      clearEditorScriptsLibrary();
    },
    openEditorScriptsDocument: () => {
      void openEditorScriptsDocument();
    },
    downloadEditorScriptsDocument: () => {
      void downloadEditorScriptsDocumentCopy();
    },
    exportSelectedFramesPdf: () => {
      void exportSelectedFramesPdf();
    },
    openRecentPath: path => {
      void (async () => {
        try {
          const file = await readOpenWithFile(path);
          await openCurvaDocument(file);
        } catch (error) {
          console.warn("Failed to open recent .curva file", path, error);
          uiStore.setMessage(error instanceof Error ? error.message : "Could not open recent file");
          void refreshNativeRecentsMenu();
        }
      })();
    },
    clearRecents: () => {
      void clearDesktopRecents()
        .then(() => refreshNativeRecentsMenu())
        .catch(error => {
          console.warn("Failed to clear recent documents", error);
        });
    },
  };

  useEffect(() => {
    if (!isNativeShell()) {
      return;
    }
    let cancelled = false;
    let dispose: (() => void) | undefined;
    void installNativeAppMenu(nativeMenuHandlersRef).then(cleanup => {
      if (cancelled) {
        cleanup();
        return;
      }
      dispose = cleanup;
    }).catch(error => {
      console.warn("Failed to install native app menu", error);
    });
    return () => {
      cancelled = true;
      dispose?.();
    };
  }, []);

  useEffect(() => {
    syncWindowTitle();
  }, [document.name, document.revision, workspace.activeSessionId, workspace.sessions]);

  useEffect(() => {
    if (!isNativeShell()) {
      return;
    }

    let cancelled = false;
    let dispose: (() => void) | undefined;
    void onWindowCloseRequested(async () => {
      persistDesktopWorkspaceSession({ flush: true });
      return confirmDiscardDirtyWorkspace("Quit anyway");
    }).then(cleanup => {
      if (cancelled) {
        cleanup();
        return;
      }
      dispose = cleanup;
    }).catch(error => {
      console.warn("Failed to bind window close confirmation", error);
    });
    return () => {
      cancelled = true;
      dispose?.();
    };
  }, []);

  useEffect(() => {
    if (!isNativeShell()) {
      const root = rootRef.current;
      if (!root) {
        return;
      }
      const handleDragOver = (event: DragEvent) => {
        if (!event.dataTransfer || !isCurvaFileDrag(event.dataTransfer)) {
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      };
      const handleDrop = (event: DragEvent) => {
        if (!event.dataTransfer || !isCurvaFileDrag(event.dataTransfer)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        void readCurvaFilesFromDataTransfer(event.dataTransfer).then(files => {
          for (const file of files) {
            void openCurvaDocument(file);
          }
        });
      };
      root.addEventListener("dragover", handleDragOver);
      root.addEventListener("drop", handleDrop);
      return () => {
        root.removeEventListener("dragover", handleDragOver);
        root.removeEventListener("drop", handleDrop);
      };
    }

    let cancelled = false;
    let disposeDrop: (() => void) | undefined;
    let disposeOpenWith: (() => void) | undefined;

    async function openNativeCurvaPaths(paths: string[]): Promise<number> {
      const curvaPaths = paths.filter(isCurvaFilePath);
      let opened = 0;
      for (const path of curvaPaths) {
        try {
          const file = await readOpenWithFile(path);
          await openCurvaDocument(file);
          opened += 1;
        } catch (error) {
          console.warn("Failed to open .curva file", path, error);
          uiStore.setMessage(error instanceof Error ? error.message : "Could not open .curva file");
        }
      }
      return opened;
    }

    async function restoreIndexedDbFallback(): Promise<void> {
      const safeMode = isSafeMode();
      try {
        const restoredDocument = safeMode ? undefined : await loadPersistedDocument();
        if (cancelled) {
          return;
        }
        if (restoredDocument) {
          historyRef.current = new CommandHistory();
          latestViewportRef.current = { ...DEFAULT_DOCUMENT_VIEWPORT, ...restoredDocument.viewport };
          latestDocumentRef.current = restoredDocument;
          documentStore.setDocument(restoredDocument);
          applySelectionSnapshot({
            activePageId: restoredDocument.pages[0]?.id,
            selectedNodeIds: [],
            primarySelectedNodeId: undefined,
            mode: "select",
            selectedVertexId: undefined,
            selectedEdgeIds: [],
            selectedHandle: undefined,
          });
          selectionStore.setActiveTool("select");
          selectionStore.setSelectedShapeKind("rectangle");
          resetDocumentScopedState();
          uiStore.setMessage("Restored document");
        } else {
          centerViewportOnDocumentPoint(0, 0);
          uiStore.setMessage(safeMode ? "Safe mode (persistence writes off)" : "New document");
        }
        await resolveDocumentFonts(latestDocumentRef.current);
        if (cancelled) {
          return;
        }
        notifyFontsResolved();
        persistenceReadyRef.current = !safeMode;
        syncActiveWorkspaceSession();
        uiStore.setDocumentLoading(false);
      } catch (error) {
        console.warn("Failed to restore persisted document", error);
        if (!cancelled) {
          centerViewportOnDocumentPoint(0, 0);
          uiStore.setMessage(isSafeMode() ? "Safe mode (persistence writes off)" : "New document");
          persistenceReadyRef.current = !isSafeMode();
          syncActiveWorkspaceSession();
          uiStore.setDocumentLoading(false);
        }
      }
    }

    void onNativeFileDrop(paths => {
      void openNativeCurvaPaths(paths);
    }).then(cleanup => {
      if (cancelled) {
        cleanup();
        return;
      }
      disposeDrop = cleanup;
    }).catch(error => {
      console.warn("Failed to bind native file drop", error);
    });

    void (async () => {
      try {
        const safeMode = isSafeMode();
        const pending = await takePendingOpenPaths();
        if (cancelled) {
          return;
        }

        let openedFromPaths = 0;
        if (!safeMode && pending.length > 0) {
          openedFromPaths = await openNativeCurvaPaths(pending);
          if (!cancelled && openedFromPaths > 0) {
            persistDesktopWorkspaceSession({ flush: true });
          }
        } else if (!safeMode) {
          const config = await loadDesktopConfig();
          if (cancelled) {
            return;
          }
          const preferredPath = config.session.paths[config.session.active];
          openedFromPaths = await openNativeCurvaPaths(config.session.paths);
          if (!cancelled && openedFromPaths > 0) {
            if (preferredPath) {
              const existing = workspaceStore.findSessionByPath(preferredPath);
              if (existing) {
                activateWorkspaceSession(existing.id);
              }
            }
            persistDesktopWorkspaceSession({ flush: true });
            uiStore.setMessage(openedFromPaths === 1 ? "Restored document" : `Restored ${openedFromPaths} documents`);
          }
        }

        if (!cancelled && openedFromPaths === 0) {
          await restoreIndexedDbFallback();
        } else if (!cancelled) {
          persistenceReadyRef.current = !safeMode;
          uiStore.setDocumentLoading(false);
        }

        const cleanup = await onOpenCurvaPaths(paths => {
          void openNativeCurvaPaths(paths);
        });
        if (cancelled) {
          cleanup();
          return;
        }
        disposeOpenWith = cleanup;
        void refreshNativeRecentsMenu();
      } catch (error) {
        console.warn("Failed to bind Open With / restore desktop session", error);
        if (!cancelled) {
          await restoreIndexedDbFallback();
        }
      }
    })();

    return () => {
      cancelled = true;
      disposeDrop?.();
      disposeOpenWith?.();
    };
  }, []);

  useEditorShellKeymap({
    rootRef,
    isDocumentLoading,
    prototypePlayerFrameId,
    scriptPreviewActive: Boolean(scriptPreview),
    animationPanelOpen,
    animationTimelineOpen,
    armedAnimationNodeKind,
    editorActions,
    canPasteVectorVertices,
    setAnimationModeOpen,
    setAnimationTimelineOpenPreference,
    resetAnimationPlayback,
  });

  useAnimationPlaybackKeymap({
    animationPanelOpen,
    animationPlaying,
    animationTimeMs,
    animationDurationMs,
    animationLoopRegion,
    pauseAnimation,
    resetAnimationPlayback,
    scriptPreviewActive: Boolean(scriptPreview),
  });

  useEditorShellClipboard({
    rootRef,
    isDocumentLoading,
    mode,
    animationPanelOpen,
    canPasteVectorVertices,
    pasteVectorVerticesFromClipboard,
    pasteSvg,
    pasteImage,
  });

  if (isDocumentLoading) {
    return (
      <div
        ref={rootRef}
        className="dark relative h-screen w-screen overflow-hidden bg-background text-foreground"
      >
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background text-slate-300">
          <div className="flex flex-col items-center gap-3">
            <div className="size-8 animate-spin rounded-full border-2 border-slate-700 border-t-sky-400" aria-hidden="true" />
            <div className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500">Loading fonts</div>
          </div>
        </div>
      </div>
    );
  }

  const activeAnimationFrames = getActivePageSceneNodes(documentStore.getDocument(), resolvedActivePageId).filter((node): node is FrameNode => node.type === "frame");

  const animationPanel = (
    <AnimationPanel
      open={animationPanelOpen}
      playing={animationPlaying}
      timeMs={animationTimeMs}
      durationMs={animationDurationMs}
      timelineOpen={animationTimelineOpen}
      loopRegion={animationLoopRegion}
      hasActiveAnimation={activeAnimationRootId !== undefined}
      animationGraph={animationGraph}
      activeAnimationRootId={activeAnimationRootId}
      selectedAnimationNodeIds={selectedAnimationNodeIds}
      primarySelectedAnimationNodeId={primarySelectedAnimationNodeId}
      armedNodeKind={armedAnimationNodeKind}
      timelineViewport={activeAnimationTimelineViewport}
      onOpenChange={open => setAnimationModeOpen(open, true)}
      onArmNodeKind={kind => uiStore.patchAnimation({ armedNodeKind: kind })}
      onPlay={() => {
        if (animationDurationMs <= 0) {
          uiStore.setMessage("No animation motion");
          return;
        }
        uiStore.patchAnimation({ panelOpen: true });
        uiStore.patchAnimation({ playing: true });
        if (
          animationLoopRegion &&
          animationLoopRegion.endMs > animationLoopRegion.startMs &&
          (animationTimeMs < animationLoopRegion.startMs || animationTimeMs >= animationLoopRegion.endMs)
        ) {
          uiStore.patchAnimation({ timeMs: Math.max(0, Math.min(animationLoopRegion.startMs, animationDurationMs)) });
        } else if (animationTimeMs >= animationDurationMs) {
          uiStore.patchAnimation({ timeMs: 0 });
        }
      }}
      onPause={pauseAnimation}
      onReset={resetAnimationPlayback}
      onTimelineOpenChange={setAnimationTimelineOpenPreference}
      onTimeChange={time => {
        uiStore.patchAnimation({ playing: false });
        uiStore.patchAnimation({ timeMs: Math.max(0, Math.min(time, animationDurationMs)) });
      }}
      onLoopRegionChange={region => uiStore.patchAnimation({ loopRegion: region })}
      onTimelineViewportChange={viewport => {
        uiStore.patchAnimation({
          timelineViewports: animationTimelineViewportsWith(
            uiStore.animation.getValue().timelineViewports,
            activeAnimationRootId,
            viewport,
          ),
        });
      }}
      onSelectAnimationNodes={(ids, primaryId, options) => {
        if (ids.length === 0) {
          activateAnimationRoot(undefined);
        } else if (options?.switchRoot) {
          activateAnimationRootForNode(primaryId ?? ids[0]);
        }
        updateSelection(selectionSnapshot({
          selectedAnimationNodeIds: ids,
          primarySelectedAnimationNodeId: primaryId,
          selectedAnimationConnectionIds: [],
        }), false);
      }}
      onUpdateMotionNode={(nodeId, changes) => {
        const node = animationGraph.nodes.find(n => n.id === nodeId);
        if (!node || node.kind !== "motion") return;
        updateAnimationNode({ ...node, ...changes }, changes.name !== undefined ? "Renamed motion" : "Updated motion");
      }}
      onExport={format => void exportActiveAnimation(format)}
      onCopyMermaid={() => void copyActiveAnimationAsMermaid()}
      onBake={bakeActiveAnimationToFrame}
      exportingFormat={animationExportingFormat}
    />
  );

  const showDocumentTabs = isNativeShell() || workspace.sessions.length > 1;

  return (
    <div
      ref={rootRef}
      className="dark relative h-screen w-screen overflow-hidden bg-background text-foreground"
    >
      <div className="absolute inset-0">
        <CanvasWorkspace
          onViewportChange={handleViewportChange}
          onZoomChange={uiStore.setZoom}
          onPointerStatusChange={uiChromeMode === "visible" ? uiStore.setPointerStatus : undefined}
          onToolChange={changeTool}
          documentActions={documentActions}
          vectorActions={vectorActions}
          animationActions={animationActions}
        />
      </div>
      {animationPanelOpen && animationPreviewWindowOpen && uiChromeMode !== "hidden" ? (
        <AnimationPreviewWindow
          animationGraph={animationGraph}
          activeAnimationRootId={activeAnimationRootId}
          animationFrames={activeAnimationFrames}
          timeMs={animationTimeMs}
          onClose={() => setAnimationPreviewWindowOpenPreference(false)}
        />
      ) : null}
      {scriptsDebugWindowOpen && uiChromeMode !== "hidden" ? (
        <ScriptsDebugWindow onClose={() => uiStore.setScriptsDebugWindowOpen(false)} />
      ) : null}
      {uiChromeMode === "tools" || uiChromeMode === "toolsSidebar" ? (
        <div className="pointer-events-none absolute inset-0 z-20">
          <FloatingToolPanel actions={topBarActions} />
          <div className={`absolute bottom-0 z-40 ${uiChromeMode === "toolsSidebar" ? "left-64 right-0" : "inset-x-0"}`}>
            {animationPanel}
          </div>
        </div>
      ) : null}
      {uiChromeMode === "visible" || uiChromeMode === "toolsSidebar" ? (
        <div className={`absolute left-0 z-10 flex min-h-0 overflow-hidden ${uiChromeMode === "visible" ? `bottom-7 ${showDocumentTabs ? "top-20" : "top-12"}` : "inset-y-0"}`}>
          <LeftPanel
            onSwitchPage={switchPage}
            onAddPage={addPage}
            onRenamePage={renamePage}
            onDeletePage={deletePage}
            onDuplicatePage={duplicatePage}
            onSelectNodes={(nodeIds, primaryNodeId) => selectNodes(nodeIds, primaryNodeId)}
            onRenameNode={renameNode}
            onVisibilityChange={updateNodeVisibility}
            onLockedChange={updateNodeLocked}
            onMoveNodes={moveNodesInLayerTree}
            onContextMenu={openContextMenu}
            onComponentPlacementStart={beginComponentPlacement}
            onLocateComponent={locateComponent}
            allowScripts={allowScripts}
            onEditScript={openAttachedFrameScriptEditor}
          />
        </div>
      ) : null}
      {uiChromeMode !== "visible" ? null : (
        <>
          <div className="absolute inset-x-0 top-0 z-20">
            {showDocumentTabs ? (
              <DocumentTabStrip
                onActivate={activateWorkspaceSession}
                onClose={requestCloseWorkspaceSession}
                onCloseMany={requestCloseWorkspaceSessions}
                onNew={requestNewDocument}
                onRename={renameWorkspaceSession}
                onSessionsReordered={() => persistDesktopWorkspaceSession()}
              />
            ) : null}
            <TopBar actions={topBarActions} />
          </div>
          <div className={`absolute bottom-7 right-0 z-10 flex min-h-0 overflow-hidden ${showDocumentTabs ? "top-20" : "top-12"}`}>
            <RightPanel
              frameTemplateCategories={FRAME_TEMPLATE_CATEGORIES}
              onBoundsChange={(nodeId, bounds) => updateNodeBounds([{ nodeId, bounds }], "Updated properties")}
              onSelectedNodeBoundsChange={updateNodeBounds}
              onSelectionBoundsChange={updateSelectionBounds}
              onSelectionRotationChange={updateSelectionRotation}
              onStyleChange={updateNodeStyle}
              onSelectedNodeStylesChange={updateSelectedNodeStyles}
              onSelectionPaintChange={updateSelectionPaint}
              onPrimitiveChange={updatePrimitiveDefinition}
              onTextChange={updateTextNode}
              onTextStylePatch={updateTextTypeStyle}
              onTextStylePreview={previewTextTypeStyle}
              onTextStylePreviewClear={clearTextTypeStylePreview}
              textFillForInspector={textFillForInspector}
              onFrameClipsContentChange={updateFrameClipsContent}
              onFrameCornerRadiiChange={updateFrameCornerRadii}
              onFrameLayoutChange={updateFrameLayout}
              onFlexChildLayoutChange={updateFlexChildLayout}
              onConstraintsChange={updateNodeConstraints}
              onAddComponentVariant={addComponentVariant}
              onDeleteComponentVariant={deleteComponentVariant}
              onInstanceVariantChange={updateInstanceVariant}
              onFrameTemplateInsert={insertFrameTemplate}
              onColorSampleRequest={requestColorSample}
              onColorSampleModeChange={updateColorSampleMode}
              onColorSampleCancel={cancelColorSample}
              onVectorVertexSettingsChange={updateVectorVertexSettings}
              onReplaceSelectionPaint={replaceSelectionPaint}
              onCreateDocumentStyle={createDocumentStyle}
              onApplyDocumentStyle={applyDocumentStyle}
              onDetachDocumentStyle={detachDocumentStyle}
              onUpdateDocumentStyle={updateDocumentStyle}
              onDeleteDocumentStyle={deleteDocumentStyle}
              onLocateNode={selectAndLocateNode}
              onPlayPrototype={playPrototype}
              allowScripts={allowScripts}
              onCreateDocumentScript={createDocumentScript}
              onCreateScriptFolder={createScriptFolder}
              onRenameScriptEntry={renameScriptEntry}
              onDuplicateDocumentScript={duplicateDocumentScript}
              onDeleteScriptEntry={deleteScriptEntry}
              onMoveScriptEntry={moveScriptEntry}
              onRunDocumentScript={scriptId => runDocumentScript(scriptId)}
              onRunEditorScript={scriptId => runEditorScript(scriptId)}
              onCopyEditorScriptToDocument={entryId => {
                runCopyEditorScriptEntryToDocument(commandHost, entryId);
              }}
            />
          </div>
          <div className="absolute inset-x-0 bottom-0 z-20">
            <StatusBar
              zoom={zoom}
              tool={
                mode === "textEdit"
                  ? "Text"
                  : mode === "vectorEdit"
                  ? activeTool === "path"
                    ? "Path"
                    : activeTool === "lasso"
                      ? "Lasso"
                    : activeTool === "fill"
                      ? "Fill"
                    : activeTool === "shapeBuilder"
                      ? "Shape Builder"
                    : activeTool === "knife"
                      ? "Knife"
                    : "Vector"
                  : activeTool === "shape"
                    ? titleCase(selectedShapeKind)
                    : activeTool === "frame"
                      ? "Frame"
                    : activeTool === "path"
                      ? "Path"
                      : activeTool === "pencil"
                        ? "Pencil"
                        : activeTool === "lasso"
                          ? "Lasso"
                          : activeTool === "text"
                            ? "Text"
                            : "Move"
              }
            />
          </div>
          <div className="absolute bottom-7 left-64 right-72 z-40">
            {animationPanel}
          </div>
        </>
      )}
      {contextMenu ? (
        <ObjectContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          selectedNodeCount={selectedNodeIds.includes(contextMenu.nodeId) ? selectedNodeIds.length : 1}
          isInstance={findSceneNode(documentStore.getDocument(), contextMenu.nodeId)?.type === "instance"}
          isMask={findSceneNode(documentStore.getDocument(), contextMenu.nodeId)?.isMask === true}
          onLocate={() => {
            locateNodeInViewport(contextMenu.nodeId);
            uiStore.setContextMenu(undefined);
          }}
          onGoToParentComponent={() => {
            locateInstanceParentVariant(contextMenu.nodeId);
          }}
          onDetachInstance={() => detachInstance(contextMenu.nodeId)}
          onGroup={() => {
            groupSelectedNodes();
            uiStore.setContextMenu(undefined);
          }}
          onWrapInFrame={() => {
            frameSelectedNodes();
            uiStore.setContextMenu(undefined);
          }}
          onToggleMask={() => toggleNodeMask(contextMenu.nodeId)}
          onCopyStyle={() => void copyNodeStyle(contextMenu.nodeId, ["fill", "stroke", "effects", "opacity", "cornerRadii"])}
          onCopyFill={() => void copyNodeStyle(contextMenu.nodeId, ["fill"])}
          onCopyStroke={() => void copyNodeStyle(contextMenu.nodeId, ["stroke"])}
          onCopyEffects={() => void copyNodeStyle(contextMenu.nodeId, ["effects"])}
          onPasteStyle={() => void pasteStyleToSelection(selectedNodeIds.includes(contextMenu.nodeId) ? selectedNodeIds : [contextMenu.nodeId])}
          onCopySvg={() => void copyNodeAs(contextMenu.nodeId, "svg")}
          onCopyPng={scale => void copyNodeAs(contextMenu.nodeId, "png", scale)}
          onFlatten={() => {
            void flattenSelectedNodes({ nodeIds: selectedNodeIds.includes(contextMenu.nodeId) ? selectedNodeIds : [contextMenu.nodeId] });
            uiStore.setContextMenu(undefined);
          }}
          onPlanarize={() => {
            void flattenSelectedNodes({ planarize: true, nodeIds: selectedNodeIds.includes(contextMenu.nodeId) ? selectedNodeIds : [contextMenu.nodeId] });
            uiStore.setContextMenu(undefined);
          }}
          onOutlineStroke={() => {
            void outlineStrokeForSelection(selectedNodeIds.includes(contextMenu.nodeId) ? selectedNodeIds : [contextMenu.nodeId]);
            uiStore.setContextMenu(undefined);
          }}
          onCreateComponent={() => createComponentFromSelection(selectedNodeIds.includes(contextMenu.nodeId) ? selectedNodeIds : [contextMenu.nodeId])}
          convertTarget={contextMenuConvertTarget(documentStore.getDocument(), contextMenu.nodeId)}
          onConvertFrameGroup={() => convertFrameGroup(contextMenu.nodeId)}
          isFrame={findSceneNode(documentStore.getDocument(), contextMenu.nodeId)?.type === "frame"}
          hasScript={frameHasScript(documentStore.getDocument(), findSceneNode(documentStore.getDocument(), contextMenu.nodeId))}
          allowScripts={allowScripts}
          onAttachOrChangeScript={() => openScriptAttachPicker(contextMenu.nodeId)}
          onEditScript={() => {
            openAttachedFrameScriptEditor(contextMenu.nodeId);
            uiStore.setContextMenu(undefined);
          }}
          onDetachScript={() => detachFrameScript(contextMenu.nodeId)}
          onRunScript={() => {
            void runFrameAttachedScript(contextMenu.nodeId);
            uiStore.setContextMenu(undefined);
          }}
          onExportSvg={() => void exportNodeAs(contextMenu.nodeId, "svg")}
          onExportPng={scale => void exportNodeAs(contextMenu.nodeId, "png", scale)}
          onClose={() => uiStore.setContextMenu(undefined)}
        />
      ) : null}
      {allowScripts && scriptAttachFrameId ? (
        <ScriptAttachPicker
          appliedScriptId={(() => {
            const node = findSceneNode(documentStore.getDocument(), scriptAttachFrameId);
            return node?.type === "frame" ? node.scriptId : undefined;
          })()}
          onCreateNew={() => createAndAttachScript(scriptAttachFrameId)}
          onCommit={scriptId => {
            setFrameScriptId(scriptAttachFrameId, scriptId);
            setScriptAttachFrameId(undefined);
          }}
          onClose={() => setScriptAttachFrameId(undefined)}
        />
      ) : null}
      {allowScripts && scriptEditorScriptId ? (
        <ScriptEditorWindow
          scriptId={scriptEditorScriptId}
          onChangeScript={updateDocumentScriptSource}
          onRenameScript={renameScriptEntry}
          onRunScript={scriptId => runDocumentScript(scriptId)}
          onClose={() => uiStore.closeScriptEditor()}
        />
      ) : null}
      {allowScripts && scriptParamsSession ? (
        <ScriptParamsModal
          session={scriptParamsSession}
          onChangeValues={values => setScriptParamsSession(current => (
            current ? { ...current, values } : current
          ))}
          onChangeLivePreview={livePreview => setScriptParamsSession(current => (
            current ? { ...current, livePreview } : current
          ))}
          onRun={() => confirmScriptParamsRun()}
          onClose={() => {
            clearScriptParamLivePreview();
            setScriptParamsSession(undefined);
          }}
        />
      ) : null}
      <SelectionPaintPickerOverlay
        activeColorSamplerSourceId={colorSampler?.sourceId}
        onChange={updateSelectionPaint}
        onColorSampleRequest={requestColorSample}
        onColorSampleModeChange={updateColorSampleMode}
        onColorSampleCancel={cancelColorSample}
      />
      {prototypePlayerFrameId ? (
        <PrototypePlayerModal
          startFrameId={prototypePlayerFrameId}
          onClose={() => uiStore.setPrototypePlayerFrameId(undefined)}
        />
      ) : null}
      {scriptPreview && scriptPreviewDocument ? (
        <ScriptPreviewModal
          frameId={scriptPreview.frameId}
          frameName={scriptPreview.frameName}
          document={scriptPreviewDocument}
          input={scriptPreviewInputRef.current}
          onClose={() => stopScriptRealtimePreview()}
        />
      ) : null}
      {componentImportProgress ? (
        <ComponentImportProgressToast progress={componentImportProgress} />
      ) : null}
    </div>
  );
}
